import {
  beginSpriteBatch,
  canvas,
  createImageTexture,
  drawQuad,
  drawSpriteRect,
  drawSpriteRotated,
  endSpriteBatch,
  gl,
  rectToNdcQuad,
  resizeCanvasToDisplaySize,
  whiteTexture,
} from "./src/gl.js";
import { approach, computeCoverUVs, getSpriteSourceSize, mapUvsToSprite } from "./src/utils.js";
import {
  computeContainedRect,
  computePanelRect,
  getCanvasPoint,
  getBoxById,
  getBoxCenterPixels,
  getBoxRectPixels,
  getBenchBaseSize,
  getPanelRectForInput,
  pixelToNormalized,
  pointInRect,
} from "./src/layout.js";
import { getAutoToggleRect, getBenchCenters, getYutButtonRect } from "./src/ui.js";
import {
  appState,
  autoPlayState,
  boardAnim,
  branchState,
  editor,
  gameState,
  initRosterTokens,
  languageState,
  debugOptions,
  moveState,
  oneMoreOverlay,
  yutAnim,
  yutResultOverlay,
  runtimeState,
  rosterState,
  scoreBags,
  scoreBursts,
  scoreEvents,
  selectionState,
  seasonState,
  separationState,
  shakeState,
  turnState,
  selectionPromptLabel,
  selectionPromptSprite,
  SELECTION_PROMPT_HEIGHT,
  SELECTION_PROMPT_TEXT_HEIGHT,
  seasonPanelLabel,
  luckyPanelLabel,
  bluePanelLabel,
  titleSprite,
  titleHelpSprite,
  loadingSpinner,
  luckyBagSprite,
  luckyBagBlueSprite,
  giftIconSprite,
  blueGiftIconSprite,
  missionButtonSprite,
  textures,
  turnBannerSprites,
  yutButtonAnim,
  yutButtonTexture,
  yutThrowState,
  yutSprites,
  restartConfig,
  winOverlay,
} from "./src/state.js";
import {
  getShakeOffset,
  isPiecesAnimating,
  maybeStartPendingRosterSpawn,
  pickSelectableGroupAtPoint,
  pickBranchChoiceAtPoint,
  clearBranchState,
  queueInitialSpawnFromState,
  renderPieces,
  renderBranchArrows,
  renderSelectionArrows,
  setMoveCompleteHandler,
  applyServerMoveEffects,
  startEntryForPieceById,
  startMoveGroupFromPath,
  updateCharacterSeparation,
  updateMoveState,
  updatePieces,
  updateShake,
} from "./src/pieces.js";
import {
  initAutoToggleLabel,
  initYutButtonLabel,
  isPlayerThrowReady,
  isTurnBannerAnimating,
  isOneMoreOverlayAnimating,
  isYutAnimating,
  isYutButtonAnimating,
  isYutResultOverlayAnimating,
  renderYutAnimation,
  renderAutoToggle,
  renderYutButton,
  renderTurnBanner,
  renderYutResultOverlay,
  renderOneMoreOverlay,
  startOneMoreOverlay,
  startTurnBanner,
  startYutButtonDrop,
  startYutThrowAnimationFromServer,
  setAutoPlayEnabled,
  toggleAutoPlay,
  updateTurnBanner,
  updateYutAnimation,
  updateYutButtonAnimation,
  updateOneMoreOverlay,
  updateYutResultOverlay,
  updateYutThrowUI,
} from "./src/yut.js";
import { queueScoreEvent, renderScoreBags, updateScoreBags } from "./src/bags.js";
import { buildAdjacency, buildStepPath, computeDistancesToStart, pickStraightNext } from "./src/graph.js";
import { loadEditorDataFromFile, renderEditorOverlay, setStartBoxId } from "./src/editor.js";
import { getAtlasFrameInfo, loadAtlas, setLanguageSprites } from "./src/assets.js";
import { isWinOverlayActive, renderWinOverlay, startWinOverlay, updateWinOverlay } from "./src/win.js";
import {
  fetchRuntimeConfig,
  getTicketRemaining,
  resetMockEconomy,
  setMockEconomy,
  sendServerAction,
  startServerSession,
} from "./src/backend.js";
import {
  getSfxOptions,
  getSfxSelection,
  getSfxState,
  initSfx,
  getSfxEntryInfo,
  playSfx,
  restoreSfxSources,
  setSfxEnabled,
  setSfxSelection,
  setSfxVolume,
  deleteSfxEntry,
  uploadSfxFile,
  unlockSfx,
} from "./src/sfx.js";

let needsRedraw = false;
let lastFrameTime = 0;
let guideOverlayEnabled = false;
let sfxUnlocked = false;
let sfxUploadTargetKey = null;
let initialTurnBannerPending = true;
let initialTurnDelayRemaining = null;
let helpPopupActive = false;
let helpMarkdownTextKo = null;
let helpMarkdownTextEn = null;
let lastScore = gameState.score;
let lastBlueScore = gameState.blueScore;
let lastFreeThrowFor = turnState.freeThrowFor;
let missionButtonPressed = false;
let redGiftPressed = false;
let blueGiftPressed = false;
let seasonEndTime = null;
let lastSeasonMinutes = null;
let seasonEnded = false;
let seasonEndFromServer = false;
let helpPopupMode = "help";
let serverActionQueue = [];
let serverPendingSnapshot = null;
let serverPendingPrompt = null;
let serverPendingMoveEffects = null;
let serverPendingFinishWait = false;
let serverPendingKnockWait = false;
let serverPendingEntryWait = false;
let lastRenderPanelRect = null;
const IDLE_BREATHE_INTERVAL_MS = 1000 / 12;
let idleBreatheTimer = null;
let fpsLastTime = 0;
let fpsFrameCount = 0;
let renderScheduled = false;
let isRendering = false;
let lastRenderTimestamp = 0;
const MAX_FPS = 30;
const MAX_FRAME_INTERVAL_MS = 1000 / MAX_FPS;
const FRAME_INTERVAL_TOLERANCE = 0.9;
const DEV_TICKET_GRANT = 1000;

function scheduleIdleBreathe() {
  if (idleBreatheTimer != null) {
    return;
  }
  idleBreatheTimer = window.setTimeout(() => {
    idleBreatheTimer = null;
    requestDraw();
  }, IDLE_BREATHE_INTERVAL_MS);
}

function cancelIdleBreathe() {
  if (idleBreatheTimer == null) {
    return;
  }
  clearTimeout(idleBreatheTimer);
  idleBreatheTimer = null;
}

function updateDebugFps(timestamp) {
  if (!debugFps || !runtimeState.debugAllowed) {
    return;
  }
  if (typeof timestamp !== "number") {
    return;
  }
  if (!fpsLastTime) {
    fpsLastTime = timestamp;
    fpsFrameCount = 0;
    return;
  }
  fpsFrameCount += 1;
  const elapsed = timestamp - fpsLastTime;
  if (elapsed < 500) {
    return;
  }
  const fps = Math.max(0, Math.round((fpsFrameCount * 1000) / elapsed));
  debugFps.textContent = `${fps} FPS`;
  fpsLastTime = timestamp;
  fpsFrameCount = 0;
}

function getCornerBoxIds() {
  if (!editor.boxes || !editor.boxes.length) {
    return [];
  }
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const box of editor.boxes) {
    minX = Math.min(minX, box.x);
    maxX = Math.max(maxX, box.x);
    minY = Math.min(minY, box.y);
    maxY = Math.max(maxY, box.y);
  }
  const targets = [
    { x: minX, y: minY },
    { x: minX, y: maxY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
  ];
  const ids = new Set();
  for (const target of targets) {
    let bestId = null;
    let bestDist = Infinity;
    for (const box of editor.boxes) {
      const dx = box.x - target.x;
      const dy = box.y - target.y;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestId = box.id;
      }
    }
    if (bestId != null) {
      ids.add(bestId);
    }
  }
  return Array.from(ids);
}

function getMinStepsToCorner(startId, adjacency, cornerSet, maxSteps = 3) {
  if (startId == null || !adjacency || !cornerSet || cornerSet.size === 0) {
    return null;
  }
  const visited = new Set([startId]);
  const queue = [{ id: startId, dist: 0 }];
  while (queue.length) {
    const current = queue.shift();
    if (current.dist > maxSteps) {
      continue;
    }
    if (current.dist > 0 && cornerSet.has(current.id)) {
      return current.dist;
    }
    const nextList = adjacency.get(current.id) || [];
    for (const nextId of nextList) {
      if (visited.has(nextId)) {
        continue;
      }
      visited.add(nextId);
      queue.push({ id: nextId, dist: current.dist + 1 });
    }
  }
  return null;
}

function getDistancesToStart(adjacency) {
  if (!adjacency || editor.startBoxId == null) {
    return null;
  }
  return computeDistancesToStart(adjacency, editor.startBoxId);
}

function hasActiveTeamOnBox(team, landingId) {
  if (landingId == null) {
    return false;
  }
  return rosterState.tokens.some(
    (token) => token.status === "active" && token.team === team && token.boxId === landingId
  );
}

function buildStepPathWithFirst(startId, steps, firstNextId) {
  const adjacency = buildAdjacency();
  const path = [];
  let current = startId;
  let previous = null;
  for (let i = 0; i < steps; i += 1) {
    let nextId = null;
    if (i === 0) {
      const nextList = adjacency.get(current) || [];
      if (!nextList.includes(firstNextId)) {
        return null;
      }
      nextId = firstNextId;
    } else {
      const nextList = adjacency.get(current) || [];
      if (nextList.length === 0) {
        break;
      }
      if (nextList.length === 1) {
        nextId = nextList[0];
      } else {
        nextId = pickStraightNext(previous, current, nextList);
      }
    }
    path.push(nextId);
    previous = current;
    current = nextId;
  }
  return path;
}

function simulateLandingForGroup(group, steps) {
  if (!group || group.boxId == null) {
    return null;
  }
  const path = buildStepPath(group.boxId, steps, editor.startBoxId);
  if (!path || !path.length) {
    return null;
  }
  let leftStart = group.pieces.some((piece) => piece.hasLeftStart);
  let previous = group.boxId;
  for (let i = 0; i < path.length; i += 1) {
    const nextId = path[i];
    if (previous === editor.startBoxId) {
      leftStart = true;
    }
    if (nextId === editor.startBoxId && leftStart) {
      return { path: path.slice(0, i + 1), landingId: nextId, finished: true };
    }
    previous = nextId;
  }
  return { path, landingId: path[path.length - 1], finished: false };
}

function simulateLandingForGroupWithFirst(group, steps, firstNextId) {
  if (!group || group.boxId == null) {
    return null;
  }
  const path = buildStepPathWithFirst(group.boxId, steps, firstNextId);
  if (!path || !path.length) {
    return null;
  }
  let leftStart = group.pieces.some((piece) => piece.hasLeftStart);
  let previous = group.boxId;
  for (let i = 0; i < path.length; i += 1) {
    const nextId = path[i];
    if (previous === editor.startBoxId) {
      leftStart = true;
    }
    if (nextId === editor.startBoxId && leftStart) {
      return { path: path.slice(0, i + 1), landingId: nextId, finished: true };
    }
    previous = nextId;
  }
  return { path, landingId: path[path.length - 1], finished: false };
}

function findAttackableChoice(group, steps, adjacency, enemyTeam) {
  if (!group || group.boxId == null) {
    return null;
  }
  const nextList = adjacency.get(group.boxId) || [];
  if (nextList.length <= 1) {
    const simulation = simulateLandingForGroup(group, steps);
    if (!simulation || simulation.finished) {
      return null;
    }
    if (hasActiveTeamOnBox(enemyTeam, simulation.landingId)) {
      return { firstNextId: null };
    }
    return null;
  }
  for (const nextId of nextList) {
    const simulation = simulateLandingForGroupWithFirst(group, steps, nextId);
    if (!simulation || simulation.finished) {
      continue;
    }
    if (hasActiveTeamOnBox(enemyTeam, simulation.landingId)) {
      return { firstNextId: nextId };
    }
  }
  return null;
}

function findCornerChoice(group, steps, adjacency, cornerSet) {
  if (!group || group.boxId == null || !cornerSet || cornerSet.size === 0) {
    return null;
  }
  const nextList = adjacency.get(group.boxId) || [];
  if (nextList.length <= 1) {
    const simulation = simulateLandingForGroup(group, steps);
    if (!simulation) {
      return null;
    }
    if (cornerSet.has(simulation.landingId)) {
      return { firstNextId: null };
    }
    return null;
  }
  for (const nextId of nextList) {
    const simulation = simulateLandingForGroupWithFirst(group, steps, nextId);
    if (!simulation) {
      continue;
    }
    if (cornerSet.has(simulation.landingId)) {
      return { firstNextId: nextId };
    }
  }
  return null;
}

function selectAutoGroup(groups, steps) {
  if (!groups.length) {
    return null;
  }
  const adjacency = buildAdjacency();
  const cornerSet = new Set(getCornerBoxIds());
  const attackable = [];
  for (const group of groups) {
    const choice = findAttackableChoice(group, steps, adjacency, "monster");
    if (choice) {
      attackable.push(group);
    }
  }
  if (attackable.length) {
    return attackable[0];
  }
  if (cornerSet.size) {
    for (const group of groups) {
      const choice = findCornerChoice(group, steps, adjacency, cornerSet);
      if (choice) {
        return group;
      }
    }
  }
  const distances = getDistancesToStart(adjacency);
  const progressed = groups.filter((group) =>
    group.pieces.some((piece) => piece.hasLeftStart)
  );
  const candidates = progressed.length ? progressed : groups;
  if (steps >= 4 && cornerSet.size && distances && candidates.length > 1) {
    let frontGroup = candidates[0];
    let backGroup = candidates[0];
    let frontDist = distances.has(frontGroup.boxId) ? distances.get(frontGroup.boxId) : -Infinity;
    let backDist = distances.has(backGroup.boxId) ? distances.get(backGroup.boxId) : Infinity;
    for (let i = 1; i < candidates.length; i += 1) {
      const group = candidates[i];
      const dist = distances.has(group.boxId) ? distances.get(group.boxId) : Infinity;
      if (dist > frontDist) {
        frontDist = dist;
        frontGroup = group;
      }
      if (dist < backDist) {
        backDist = dist;
        backGroup = group;
      }
    }
    if (frontGroup && backGroup && frontGroup !== backGroup) {
      const stepsToCorner = getMinStepsToCorner(frontGroup.boxId, adjacency, cornerSet, 3);
      if (stepsToCorner != null) {
        return backGroup;
      }
    }
  }
  let bestGroup = null;
  let bestDist = null;
  for (const group of candidates) {
    if (!group || group.boxId == null) {
      continue;
    }
    let groupBest = null;
    if (distances && distances.has(group.boxId)) {
      groupBest = distances.get(group.boxId);
    }
    if (groupBest == null) {
      continue;
    }
    if (bestDist == null || groupBest < bestDist) {
      bestDist = groupBest;
      bestGroup = group;
    }
  }
  return bestGroup || groups[0];
}

function selectAutoBranch() {
  if (!branchState.active) {
    return null;
  }
  const candidates = Array.isArray(branchState.candidates) ? branchState.candidates : [];
  if (!candidates.length) {
    return null;
  }
  const choices =
    Array.isArray(branchState.choices) && branchState.choices.length
      ? branchState.choices
      : candidates.map((nextId) => ({ nextId, landingId: nextId, enemy: false }));
  const enemyChoice = choices.find((choice) => choice.enemy);
  if (enemyChoice) {
    return enemyChoice.nextId;
  }
  const adjacency = buildAdjacency();
  const cornerSet = new Set(getCornerBoxIds());
  const distances = getDistancesToStart(adjacency);
  const cornerChoices = choices.filter((choice) => cornerSet.has(choice.landingId));
  if (cornerChoices.length) {
    let bestCorner = null;
    let bestCornerDist = null;
    for (const choice of cornerChoices) {
      const landingId = Number(choice.landingId);
      if (!distances || !Number.isFinite(landingId) || !distances.has(landingId)) {
        if (bestCorner == null) {
          bestCorner = choice;
        }
        continue;
      }
      const dist = distances.get(landingId);
      if (bestCornerDist == null || dist < bestCornerDist) {
        bestCornerDist = dist;
        bestCorner = choice;
      }
    }
    if (bestCorner) {
      return bestCorner.nextId;
    }
  }
  let bestChoice = null;
  let bestDist = null;
  for (const choice of choices) {
    const nextId = Number(choice.nextId);
    if (!Number.isFinite(nextId)) {
      continue;
    }
    if (!distances || !distances.has(nextId)) {
      if (bestChoice == null) {
        bestChoice = choice;
      }
      continue;
    }
    const dist = distances.get(nextId);
    if (bestDist == null || dist < bestDist) {
      bestDist = dist;
      bestChoice = choice;
    }
  }
  return bestChoice ? bestChoice.nextId : choices[0].nextId;
}

function summarizeServerAction(action) {
  if (!action || typeof action !== "object") {
    return action;
  }
  if (action.type === "move") {
    const effects = action.effects || {};
    return {
      type: "move",
      team: action.team,
      groupId: action.groupId,
      from: Number.isFinite(action.from_box_id)
        ? action.from_box_id
        : Number.isFinite(action.fromBoxId)
          ? action.fromBoxId
          : null,
      path: Array.isArray(action.path) ? action.path.slice() : [],
      effects: {
        landingId: Number.isFinite(effects.landingId) ? effects.landingId : null,
        knockIds: Array.isArray(effects.knockIds) ? effects.knockIds.slice() : [],
        finish: Array.isArray(effects.finish) ? effects.finish.map((entry) => entry.id) : [],
        bonusStreak: Number(effects.bonusStreak) || 0,
      },
    };
  }
  if (action.type === "yut") {
    return {
      type: "yut",
      owner: action.owner,
      key: action.key,
      steps: Number(action.steps) || 0,
    };
  }
  if (action.type === "overlay") {
    return { type: "overlay", team: action.team, variant: action.variant };
  }
  if (action.type === "entry") {
    return { type: "entry", team: action.team, pieceId: action.pieceId ?? action.piece_id ?? null };
  }
  if (action.type === "reset") {
    return { type: "reset" };
  }
  return action;
}
let serverSpawnInitialized = false;
const TITLE_FADEIN_DURATION = 0.6;
const TITLE_APPEAR_DURATION = 0.35;
const TITLE_DOCK_DURATION = 0.6;
const TITLE_FLOAT_SPEED = 1.2;
const TITLE_HEIGHT_RATIO = 0.18;
const TITLE_EN_WIDTH_SCALE = 0.9;
const LOADING_SPINNER_RATIO = 0.12;
const TITLE_CORNER_SCALE = 0.4;
const TITLE_CORNER_MARGIN = 0.04;
const TITLE_CORNER_OFFSET_Y = -0.015;
const TITLE_HELP_SCALE = 0.7;
const TITLE_HELP_GAP = 0.08;
const SIDE_PANEL_WIDTH_RATIO = 0.16;
const SIDE_PANEL_WIDTH_EXTEND_RATIO = 0.045;
const RESTART_BUTTON_WIDTH_RATIO = 0.36;
const RESTART_BUTTON_MIN_WIDTH = 160;
const RESTART_BUTTON_MAX_WIDTH = 320;
const SIDE_PANEL_GAP_RATIO = 0.012;
const SIDE_PANEL_OFFSET_RATIO = 0.02;
const SEASON_PANEL_HEIGHT_RATIO = 0.09;
const LUCKY_PANEL_HEIGHT_RATIO = 0.075;
const SIDE_PANEL_PAD_RATIO = 0.055;
const LUCKY_PANEL_ICON_SLOT_RATIO = 0.24;
const LUCKY_PANEL_GIFT_RATIO = 0.18;
const LUCKY_PANEL_GIFT_RADIUS = 0.18;
const SEASON_TITLE_TEXT_KO = "\ub0a8\uc740 \uc2dc\uc98c \uae30\uac04";
const SEASON_TITLE_TEXT_EN = "Season Remaining";
const SEASON_TIME_TEXT_KO = "10\uc2dc\uac04 5\ubd84 \ub0a8\uc74c";
const SEASON_TIME_TEXT_EN = "10h 5m left";
const titleState = {
  phase: "loading",
  elapsed: 0,
  floatTime: 0,
};
let selectionPromptAlpha = 0;

function getHashbangParams() {
  const hash = window.location.hash || "";
  let raw = hash.startsWith("#!") ? hash.slice(2) : hash.startsWith("#") ? hash.slice(1) : hash;
  if (raw.startsWith("?")) {
    raw = raw.slice(1);
  }
  return new URLSearchParams(raw);
}

function normalizeUnityMode(value) {
  if (!value) {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized.startsWith("editor")) {
    return "editor";
  }
  if (normalized.startsWith("build")) {
    return "build";
  }
  return null;
}

function getUnityModeFromUrl() {
  const params = new URLSearchParams(window.location.search || "");
  const hashParams = getHashbangParams();
  const raw = params.get("unity") ?? hashParams.get("unity");
  return normalizeUnityMode(raw);
}

function getSessionKeyFromUrl() {
  const params = new URLSearchParams(window.location.search || "");
  const hashParams = getHashbangParams();
  const raw = params.get("session_key") ?? hashParams.get("session_key");
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  return trimmed.length ? trimmed : null;
}

function sendToUnity(path, params = {}) {
  if (!runtimeState.unity) {
    console.info("[unity] missing unity param; skip message", { path, params });
    return false;
  }
  const queryString = new URLSearchParams(params).toString();
  const url = `uniwebview://${path}?${queryString}`;
  window.location.href = url;
  return true;
}

function onClickShop() {
  setAutoPlayEnabled(false);
  sendToUnity("popup", { type: "eventShop", data: "{}" });
}

function onClickRewards() {
  setAutoPlayEnabled(false);
  sendToUnity("popup", { type: "eventRewards", data: "{}" });
}

function onClickMissions() {
  setAutoPlayEnabled(false);
  sendToUnity("popup", { type: "eventMissions", data: "{}" });
}

function initRuntimeFromUrl() {
  const unityMode = getUnityModeFromUrl();
  runtimeState.unity = unityMode;
  const sessionKey = getSessionKeyFromUrl();
  runtimeState.sessionKey = sessionKey;
  runtimeState.useStub = false;
}

function syncGuideButtonUi() {
  if (!guideButton) {
    return;
  }
  guideButton.classList.toggle("is-active", guideOverlayEnabled);
  guideButton.setAttribute("aria-pressed", guideOverlayEnabled ? "true" : "false");
  guideButton.textContent = guideOverlayEnabled ? "GUIDE ON" : "GUIDE";
}

function setGuideOverlayEnabled(next) {
  guideOverlayEnabled = !!next;
  syncGuideButtonUi();
  requestDraw();
}

function updateOptionsDebugToggle() {
  if (!optionsDebugToggle) {
    return;
  }
  const active = debugUiEnabled;
  optionsDebugToggle.classList.toggle("is-active", active);
  optionsDebugToggle.setAttribute("aria-pressed", active ? "true" : "false");
  optionsDebugToggle.textContent = active ? "ON" : "OFF";
}

function setDebugControlsVisible(enabled) {
  const controls = [debugButton, guideButton];
  for (const control of controls) {
    if (control) {
      control.style.display = enabled ? "" : "none";
    }
  }
  if (enabled) {
    syncGuideButtonUi();
    return;
  }
  if (guideOverlayEnabled) {
    setGuideOverlayEnabled(false);
  }
  setDebugPanelsOpen(false);
  if (debugYutButtons) {
    debugYutButtons.classList.remove("is-active");
    debugYutButtons.setAttribute("aria-hidden", "true");
  }
}

function setDebugUiVisible(allowed) {
  if (debugFps) {
    debugFps.style.display = allowed ? "" : "none";
    debugFps.setAttribute("aria-hidden", allowed ? "false" : "true");
  }
  if (optionsDebugRow) {
    optionsDebugRow.style.display = allowed ? "" : "none";
  }
  if (!allowed) {
    fpsLastTime = 0;
    fpsFrameCount = 0;
    editor.enabled = false;
    setDebugControlsVisible(false);
    updateOptionsDebugToggle();
    return;
  }
  setDebugControlsVisible(debugUiEnabled);
  updateOptionsDebugToggle();
}

function setMockDebugAvailability() {
  const enabled = runtimeState.debugAllowed && runtimeState.useStub;
  const controls = [
    mockTicketInput,
    mockRedBagInput,
    mockBlueBagInput,
    mockApplyButton,
    mockResetButton,
  ];
  for (const control of controls) {
    if (control) {
      control.disabled = !enabled;
    }
  }
  if (mockUserSection) {
    mockUserSection.classList.toggle("is-disabled", !enabled);
  }
}

function syncMockDebugInputs() {
  if (!runtimeState.debugAllowed || !runtimeState.useStub) {
    return;
  }
  const remaining = getTicketRemaining();
  if (mockTicketInput) {
    mockTicketInput.value = `${remaining}`;
  }
  if (mockRedBagInput) {
    mockRedBagInput.value = `${Math.max(0, Math.round(gameState.score))}`;
  }
  if (mockBlueBagInput) {
    mockBlueBagInput.value = `${Math.max(0, Math.round(gameState.blueScore))}`;
  }
}

async function bootstrapRuntime() {
  await fetchRuntimeConfig();
  setDebugUiVisible(runtimeState.debugAllowed);
  setOptionsUiVisible(runtimeState.debugAllowed);
  const session = await startServerSession();
  if (!session || !session.ok) {
    runtimeState.useStub = false;
    runtimeState.actionBusy = true;
    runtimeState.currentPrompt = null;
    setMockDebugAvailability();
    syncMockDebugInputs();
    requestDraw();
    return;
  }
  runtimeState.useStub = typeof session.use_stub === "boolean" ? session.use_stub : false;
  setMockDebugAvailability();
  const snapshot = session.snapshot;
  applyServerSnapshot(snapshot);
  applyServerPrompt(session.prompt);
  applyEventEndFromServer(session.event_end_at);
  if (!serverSpawnInitialized) {
    const goalCounts = snapshot?.game?.goalCounts || {};
    const shouldSpawn =
      (snapshot?.game?.playerTurnCount || 0) === 0 &&
      !snapshot?.game?.winner &&
      (goalCounts.player || 0) === 0 &&
      (goalCounts.monster || 0) === 0;
    if (shouldSpawn) {
      queueInitialSpawnFromState();
    }
    serverSpawnInitialized = true;
  }
  if (runtimeState.debugAllowed) {
    initRestartBonusInput();
    initDebugOptions();
    initMockControls();
  }
  syncMockDebugInputs();
  if (runtimeState.useStub && runtimeState.debugAllowed) {
    // DEV ONLY: give generous tickets for browser-only stub sessions.
    await setMockEconomy({ ticket_count: DEV_TICKET_GRANT });
  }
  if (runtimeState.sessionId) {
    maybeRequestServerAdvance();
  }
}

function applyServerSnapshot(snapshot) {
  if (!snapshot) {
    return;
  }
  const prevWinner = gameState.winner;
  const prevTurn = turnState.current;
  if (snapshot.game) {
    gameState.playerTurnCount = Number(snapshot.game.playerTurnCount) || 0;
    gameState.winner = snapshot.game.winner || null;
    if (snapshot.game.goalCounts) {
      gameState.goalCounts = {
        player: Number(snapshot.game.goalCounts.player) || 0,
        monster: Number(snapshot.game.goalCounts.monster) || 0,
      };
    }
  }
  if (snapshot.turn) {
    turnState.current = snapshot.turn.current === "monster" ? "monster" : "player";
    turnState.bonusThrowCount = snapshot.turn.bonusThrowCount || { player: 0, monster: 0 };
    turnState.bonusThrowStreak = snapshot.turn.bonusThrowStreak || { player: 0, monster: 0 };
    turnState.freeThrowFor = snapshot.turn.freeThrowFor === "player" ? "player" : null;
    turnState.awaitingMonster = false;
    turnState.monsterDelayRemaining = 0;
  }
  if (snapshot.economy) {
    gameState.score = Number(snapshot.economy.red_bag) || 0;
    gameState.blueScore = Number(snapshot.economy.blue_bag) || 0;
    yutThrowState.total = Math.max(0, Number(snapshot.economy.ticket_count) || 0);
    yutThrowState.used = 0;
    appState.onEconomySync?.();
  }
  if (Array.isArray(snapshot.tokens)) {
    const map = new Map(snapshot.tokens.map((token) => [token.id, token]));
    for (const token of rosterState.tokens) {
      const next = map.get(token.id);
      if (!next) {
        continue;
      }
      token.status = next.status || token.status;
      token.boxId = next.boxId != null ? next.boxId : null;
      token.hasLeftStart = !!next.hasLeftStart;
      token.goalIndex = Number.isFinite(next.goalIndex) ? next.goalIndex : null;
    }
  }
  updateYutThrowUI();
  if (prevTurn !== turnState.current && turnBannerSprites.player.texture) {
    startTurnBanner(turnState.current);
  }
  if (!prevWinner && gameState.winner) {
    startWinOverlay(gameState.winner, false);
  }
  requestDraw();
}

function buildGroupFromPrompt(group) {
  let boxId = group.boxId;
  if (boxId == null && typeof group.id === "string") {
    const parts = group.id.split(":");
    if (parts.length > 1) {
      const parsed = Number(parts[1]);
      if (Number.isFinite(parsed)) {
        boxId = parsed;
      }
    }
  }
  const pieces = [];
  if (Array.isArray(group.pieceIds)) {
    for (const id of group.pieceIds) {
      const token = rosterState.tokens.find((piece) => piece.id === id);
      if (token) {
        pieces.push(token);
      }
    }
  }
  return {
    id: group.id,
    team: group.team,
    boxId,
    pieces,
  };
}

function applyServerPrompt(prompt) {
  runtimeState.currentPrompt = prompt || null;
  selectionState.active = false;
  selectionState.steps = 0;
  selectionState.groups = [];
  clearBranchState();
  if (!prompt) {
    requestDraw();
    return;
  }
  if (prompt.type === "select_move" && Array.isArray(prompt.groups)) {
    selectionState.active = true;
    selectionState.steps = Number(prompt.steps) || 0;
    selectionState.groups = prompt.groups.map(buildGroupFromPrompt);
  } else if (prompt.type === "select_branch") {
    const group = buildGroupFromPrompt({
      id: prompt.groupId,
      team: "player",
      boxId: Number.isFinite(prompt.boxId) ? prompt.boxId : null,
      pieceIds: Array.isArray(prompt.pieceIds) ? prompt.pieceIds : [],
    });
    branchState.active = true;
    branchState.team = group.team;
    branchState.steps = Number(prompt.steps) || 0;
    branchState.group = group;
    branchState.fromBoxId = group.boxId;
    branchState.candidates = Array.isArray(prompt.candidates) ? prompt.candidates.slice() : [];
    branchState.choices = Array.isArray(prompt.choices)
      ? prompt.choices
          .map((entry) => ({
            nextId: Number(entry.nextId),
            landingId: Number(entry.landingId),
            enemy: !!entry.enemy,
          }))
          .filter((entry) => Number.isFinite(entry.nextId) && Number.isFinite(entry.landingId))
      : [];
  }
  requestDraw();
}

function queueServerRewards(rewards, panelRect) {
  if (!rewards || !panelRect) {
    return;
  }
  for (const reward of rewards) {
    if (!reward || !Number.isFinite(reward.amount) || reward.amount <= 0) {
      continue;
    }
    let origin = { x: 0.5, y: 0.5 };
    if (reward.origin && reward.origin.type === "box" && Number.isFinite(reward.origin.boxId)) {
      const box = getBoxById(reward.origin.boxId);
      if (box) {
        const center = getBoxCenterPixels(box, panelRect);
        origin = pixelToNormalized(center, panelRect);
      }
    }
    const options = {
      sync: false,
      targetKey: reward.key === "blue" ? "blue" : "red",
      spriteKey: reward.key === "blue" ? "blue" : null,
      applyScore: false,
    };
    queueScoreEvent(reward.amount, origin, options);
  }
}

function finalizeServerSequence() {
  runtimeState.actionBusy = false;
  if (serverPendingSnapshot) {
    applyServerSnapshot(serverPendingSnapshot);
  }
  serverPendingSnapshot = null;
  applyServerPrompt(serverPendingPrompt);
  serverPendingPrompt = null;
  maybeRequestServerAdvance();
}

function resetClientViewStateForServer() {
  serverPendingFinishWait = false;
  serverPendingKnockWait = false;
  serverPendingEntryWait = false;
  moveState.active = false;
  moveState.team = null;
  moveState.pieces = [];
  moveState.path = [];
  moveState.stepIndex = 0;
  moveState.progress = 0;
  moveState.from = null;
  moveState.to = null;
  selectionState.active = false;
  selectionState.steps = 0;
  selectionState.groups = [];
  clearBranchState();
  yutAnim.state = "idle";
  yutAnim.frame = 0;
  yutAnim.elapsed = 0;
  yutAnim.frames = null;
  yutAnim.resultKey = null;
  yutAnim.resultSteps = 0;
  yutAnim.moveQueued = false;
  yutAnim.scoreAwarded = false;
  yutResultOverlay.active = false;
  yutResultOverlay.elapsed = 0;
  yutResultOverlay.holdElapsed = 0;
  oneMoreOverlay.active = false;
  oneMoreOverlay.elapsed = 0;
  oneMoreOverlay.holdElapsed = 0;
  winOverlay.active = false;
  winOverlay.team = null;
  winOverlay.elapsed = 0;
  winOverlay.textElapsed = 0;
  winOverlay.floatTime = 0;
  scoreEvents.length = 0;
  scoreBags.length = 0;
  scoreBursts.length = 0;
  turnState.awaitingMonster = false;
  turnState.monsterDelayRemaining = 0;
  requestDraw();
}

function processNextServerAction() {
  if (!serverActionQueue.length) {
    finalizeServerSequence();
    return;
  }
  const action = serverActionQueue.shift();
  if (!action) {
    processNextServerAction();
    return;
  }
  const panelRect = getPanelRectForInput();
  if (action.type === "reset") {
    resetClientViewStateForServer();
    processNextServerAction();
    return;
  }
  if (action.type === "yut") {
    appState.onYutHold = () => {
      appState.onYutHold = null;
      processNextServerAction();
    };
    const started = startYutThrowAnimationFromServer(action);
    if (Array.isArray(action.rewards)) {
      queueServerRewards(action.rewards, panelRect);
    }
    if (!started) {
      appState.onYutHold = null;
      processNextServerAction();
    }
    return;
  }
  if (action.type === "move") {
    const fromBoxId = Number.isFinite(action.from_box_id)
      ? action.from_box_id
      : Number.isFinite(action.fromBoxId)
        ? action.fromBoxId
        : null;
    const pieces = Array.isArray(action.pieceIds)
      ? action.pieceIds.map((id) => rosterState.tokens.find((piece) => piece.id === id)).filter(Boolean)
      : [];
    const group = {
      id: action.groupId,
      team: action.team,
      boxId: fromBoxId != null ? fromBoxId : pieces.length ? pieces[0].boxId : null,
      pieces,
    };
    if (fromBoxId != null) {
      for (const piece of pieces) {
        if (piece.status !== "active" || piece.boxId !== fromBoxId) {
          piece.status = "active";
          piece.boxId = fromBoxId;
        }
      }
    }
    serverPendingMoveEffects = action.effects ? { ...action.effects, team: action.team } : null;
    if (!startMoveGroupFromPath(group, action.path, panelRect)) {
      serverPendingMoveEffects = null;
      processNextServerAction();
    }
    return;
  }
  if (action.type === "entry") {
    const pieceId = action.pieceId ?? action.piece_id;
    if (pieceId && startEntryForPieceById(pieceId, panelRect)) {
      serverPendingEntryWait = true;
      runtimeState.actionBusy = true;
      return;
    }
    processNextServerAction();
    return;
  }
  if (action.type === "overlay") {
    startOneMoreOverlay(action.team, action.variant);
    processNextServerAction();
    return;
  }
  processNextServerAction();
}

function handleServerMoveComplete() {
  const panelRect = getPanelRectForInput();
  let waitForFinish = false;
  let waitForKnock = false;
  if (serverPendingMoveEffects) {
    applyServerMoveEffects(serverPendingMoveEffects, panelRect);
    if (Array.isArray(serverPendingMoveEffects.rewards)) {
      queueServerRewards(serverPendingMoveEffects.rewards, panelRect);
    }
    if (serverPendingMoveEffects.bonusStreak) {
      const team = serverPendingMoveEffects.team || turnState.current;
      startOneMoreOverlay(team, serverPendingMoveEffects.bonusStreak);
    }
    waitForFinish = Array.isArray(serverPendingMoveEffects.finish) && serverPendingMoveEffects.finish.length > 0;
    waitForKnock = Array.isArray(serverPendingMoveEffects.knockIds) && serverPendingMoveEffects.knockIds.length > 0;
  }
  serverPendingMoveEffects = null;
  if (waitForFinish && rosterState.tokens.some((piece) => piece.finishActive)) {
    serverPendingFinishWait = true;
  }
  if (waitForKnock && rosterState.tokens.some((piece) => piece.knockActive)) {
    serverPendingKnockWait = true;
  }
  if (serverPendingFinishWait || serverPendingKnockWait) {
    runtimeState.actionBusy = true;
    return;
  }
  processNextServerAction();
}

function maybeResumeAfterFinish() {
  if (!serverPendingFinishWait && !serverPendingKnockWait && !serverPendingEntryWait) {
    return;
  }
  const hasFinish = rosterState.tokens.some((piece) => piece.finishActive);
  const hasKnock = rosterState.tokens.some((piece) => piece.knockActive);
  const hasEntry = rosterState.tokens.some((piece) => piece.entryActive);
  if (
    (serverPendingFinishWait && hasFinish) ||
    (serverPendingKnockWait && hasKnock) ||
    (serverPendingEntryWait && hasEntry)
  ) {
    return;
  }
  if (!hasFinish) {
    serverPendingFinishWait = false;
  }
  if (!hasKnock) {
    serverPendingKnockWait = false;
  }
  if (!hasEntry) {
    serverPendingEntryWait = false;
  }
  if (serverPendingFinishWait || serverPendingKnockWait || serverPendingEntryWait) {
    return;
  }
  processNextServerAction();
}

function maybeAutoThrow() {
  if (!autoPlayState.enabled) {
    return;
  }
  if (!isPlayerThrowReady()) {
    return;
  }
  requestServerAction({ type: "throw" });
}

function maybeAutoSelect() {
  if (!autoPlayState.enabled || runtimeState.actionBusy) {
    return;
  }
  if (!isPromptUiReady()) {
    return;
  }
  if (branchState.active) {
    const nextId = selectAutoBranch();
    if (nextId != null) {
      clearBranchState();
      requestServerAction({ type: "select_branch", next_id: nextId });
    }
    return;
  }
  if (!selectionState.active) {
    return;
  }
  const groups =
    selectionState.groups && selectionState.groups.length ? selectionState.groups : [];
  if (!groups.length) {
    return;
  }
  const group = selectAutoGroup(groups, selectionState.steps);
  if (!group) {
    return;
  }
  selectionState.active = false;
  selectionState.steps = 0;
  selectionState.groups = [];
  requestServerAction({ type: "select_move", group_id: group.id });
}

let serverAdvancePending = false;
let serverActionPending = false;
async function maybeRequestServerAdvance() {
  if (serverAdvancePending || runtimeState.actionBusy) {
    return;
  }
  if (!runtimeState.currentPrompt || runtimeState.currentPrompt.type !== "wait") {
    return;
  }
  serverAdvancePending = true;
  const response = await sendServerAction({ type: "advance" });
  serverAdvancePending = false;
  if (response && response.ok) {
    enqueueServerResponse(response);
  }
}

async function requestServerAction(action) {
  if (serverActionPending || runtimeState.actionBusy) {
    return;
  }
  runtimeState.actionBusy = true;
  serverActionPending = true;
  const response = await sendServerAction(action);
  serverActionPending = false;
  if (response && response.ok) {
    enqueueServerResponse(response);
    return;
  }
  runtimeState.actionBusy = false;
}

function enqueueServerResponse(response) {
  if (!response || !response.ok) {
    runtimeState.actionBusy = false;
    return;
  }
  if (response.event_end_at) {
    applyEventEndFromServer(response.event_end_at);
  }
  serverActionQueue = Array.isArray(response.actions) ? response.actions.slice() : [];
  serverPendingSnapshot = response.snapshot || null;
  serverPendingPrompt = response.prompt || null;
  const promptType = serverPendingPrompt ? serverPendingPrompt.type : null;
  const snapshotInfo = serverPendingSnapshot
    ? {
        turn: serverPendingSnapshot.turn?.current || null,
        winner: serverPendingSnapshot.game?.winner || null,
      }
    : null;
  const actionSummary = serverActionQueue.map(summarizeServerAction);
  if (runtimeState.debugAllowed) {
    console.info("[server] response", {
      actions: actionSummary,
      prompt: promptType,
      snapshot: snapshotInfo,
    });
  }
  runtimeState.actionBusy = serverActionQueue.length > 0;
  if (serverActionQueue.length) {
    processNextServerAction();
  } else {
    finalizeServerSequence();
  }
}

function applySfxVolumeFromUrl() {
  const params = new URLSearchParams(window.location.search || "");
  const hashParams = getHashbangParams();
  const raw = params.get("sfx_volume") ?? hashParams.get("sfx_volume");
  if (raw == null || raw === "") {
    return;
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return;
  }
  const clamped = Math.max(0, Math.min(100, value));
  initSfx();
  setSfxVolume(clamped / 100);
  updateSfxControls();
}

function normalizeLanguage(value) {
  if (!value) {
    return "ko";
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized.startsWith("en")) {
    return "en";
  }
  if (normalized.startsWith("ko")) {
    return "ko";
  }
  return "ko";
}

function getLanguageFromUrl() {
  const params = new URLSearchParams(window.location.search || "");
  const hashParams = getHashbangParams();
  const raw = params.get("lang") ?? hashParams.get("lang");
  if (!raw) {
    return null;
  }
  return normalizeLanguage(raw);
}

function getRestartAtlasKey() {
  return languageState.current === "en" ? "btn_restart_en.png" : "btn_restart_ko.png";
}

function updateWinRestartAtlas() {
  if (!winRestartButton) {
    return;
  }
  const info = getAtlasFrameInfo(getRestartAtlasKey());
  if (!info || !info.frame || !info.pageSrc || !info.pageWidth || !info.pageHeight) {
    return;
  }
  const viewportWidth = Math.max(1, window.innerWidth || 0);
  const maxWidth = Math.min(RESTART_BUTTON_MAX_WIDTH, viewportWidth * 0.9);
  const minWidth = Math.min(RESTART_BUTTON_MIN_WIDTH, viewportWidth * 0.6);
  const width = Math.max(
    minWidth,
    Math.min(viewportWidth * RESTART_BUTTON_WIDTH_RATIO, maxWidth)
  );
  const scale = width / info.frame.w;
  const height = info.frame.h * scale;
  winRestartButton.style.width = `${width}px`;
  winRestartButton.style.height = `${height}px`;
  winRestartButton.style.backgroundImage = `url("${info.pageSrc}")`;
  winRestartButton.style.backgroundRepeat = "no-repeat";
  winRestartButton.style.backgroundSize = `${info.pageWidth * scale}px ${info.pageHeight * scale}px`;
  winRestartButton.style.backgroundPosition = `${-info.frame.x * scale}px ${-info.frame.y * scale}px`;
  winRestartButton.setAttribute(
    "aria-label",
    languageState.current === "en" ? "Restart" : "다시 시작"
  );
  winRestartLayoutDirty = false;
}

function requestWinRestartLayout() {
  winRestartLayoutDirty = true;
  updateWinRestartAtlas();
}

function applyLanguage(next) {
  const normalized = normalizeLanguage(next);
  if (languageState.current === normalized) {
    return;
  }
  languageState.current = normalized;
  if (optionsLanguageSelect) {
    optionsLanguageSelect.value = normalized;
  }
  updateSelectionPromptLabelTexture();
  updateSeasonPanelLabelTexture();
  requestWinRestartLayout();
  setAutoPlayEnabled(autoPlayState.enabled);
  setLanguageSprites(normalized).then(() => {
    updateYutThrowUI();
    requestDraw();
  });
  if (helpPopupActive) {
    if (helpPopupMode === "alert") {
      populateHelpFromMarkdown(getSeasonEndMessage(), "alert");
    } else {
      populateHelpFromMarkdown(getHelpMarkdown());
    }
    updateHelpHeader();
  }
}

function initLanguageOptions() {
  const urlLang = getLanguageFromUrl();
  const initial = normalizeLanguage(urlLang || "ko");
  languageState.current = initial;
  requestWinRestartLayout();
  if (optionsLanguageSelect) {
    optionsLanguageSelect.value = initial;
    optionsLanguageSelect.addEventListener("change", () => {
      applyLanguage(optionsLanguageSelect.value);
    });
  }
  setLanguageSprites(initial).then(() => {
    updateSelectionPromptLabelTexture();
    updateSeasonPanelLabelTexture();
    updateYutThrowUI();
    requestDraw();
  });
}

function requestDraw() {
  cancelIdleBreathe();
  needsRedraw = true;
  if (isRendering || renderScheduled) {
    return;
  }
  renderScheduled = true;
  requestAnimationFrame(render);
}

function ensureSfxUnlocked() {
  if (sfxUnlocked) {
    return;
  }
  sfxUnlocked = true;
  unlockSfx();
}

appState.requestDraw = requestDraw;
appState.onEconomySync = () => {
  updateYutThrowUI();
  syncMockDebugInputs();
};
appState.onAtlasReady = () => {
  requestWinRestartLayout();
};
setMoveCompleteHandler((team) => {
  handleServerMoveComplete();
});

function loadLoadingSpinner() {
  const img = new Image();
  img.src = "assets/image/ic_yut_front.png";
  img.onload = () => {
    loadingSpinner.texture = createImageTexture(gl, img);
    loadingSpinner.width = img.width;
    loadingSpinner.height = img.height;
    loadingSpinner.sourceWidth = img.width;
    loadingSpinner.sourceHeight = img.height;
    requestDraw();
  };
}

function initSelectionPromptLabel() {
  selectionPromptLabel.canvas = document.createElement("canvas");
  selectionPromptLabel.canvas.width = selectionPromptLabel.width;
  selectionPromptLabel.canvas.height = selectionPromptLabel.height;
  selectionPromptLabel.ctx = selectionPromptLabel.canvas.getContext("2d");
  updateSelectionPromptLabelTexture();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      updateSelectionPromptLabelTexture();
      requestDraw();
    });
  }
}

function initSidePanelLabels() {
  seasonPanelLabel.canvas = document.createElement("canvas");
  seasonPanelLabel.canvas.width = seasonPanelLabel.width;
  seasonPanelLabel.canvas.height = seasonPanelLabel.height;
  seasonPanelLabel.ctx = seasonPanelLabel.canvas.getContext("2d");
  luckyPanelLabel.canvas = document.createElement("canvas");
  luckyPanelLabel.canvas.width = luckyPanelLabel.width;
  luckyPanelLabel.canvas.height = luckyPanelLabel.height;
  luckyPanelLabel.ctx = luckyPanelLabel.canvas.getContext("2d");
  bluePanelLabel.canvas = document.createElement("canvas");
  bluePanelLabel.canvas.width = bluePanelLabel.width;
  bluePanelLabel.canvas.height = bluePanelLabel.height;
  bluePanelLabel.ctx = bluePanelLabel.canvas.getContext("2d");
  updateSidePanelLabels();
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      updateSidePanelLabels();
      requestDraw();
    });
  }
}

function loadHelpMarkdown() {
  const koFetch = fetch("assets/help.md")
    .then((response) => (response.ok ? response.text() : null))
    .then((text) => {
      if (typeof text === "string") {
        helpMarkdownTextKo = text;
      }
    })
    .catch(() => {
      // Ignore help load errors.
    });
  const enFetch = fetch("assets/help_en.md")
    .then((response) => (response.ok ? response.text() : null))
    .then((text) => {
      if (typeof text === "string") {
        helpMarkdownTextEn = text;
      }
    })
    .catch(() => {
      // Ignore help load errors.
    });
  Promise.all([koFetch, enFetch]).then(() => {
    if (helpPopupActive) {
      populateHelpFromMarkdown(getHelpMarkdown());
      updateHelpHeader();
    }
  });
}

function updateSelectionPromptLabelTexture() {
  if (!selectionPromptLabel.ctx) {
    return;
  }
  const ctx = selectionPromptLabel.ctx;
  const width = selectionPromptLabel.canvas.width;
  const height = selectionPromptLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${Math.round(height * 0.42)}px NanumSquareB`;
  const message = getSelectionPromptMessage();
  ctx.fillText(message, width * 0.5, height * 0.5);

  if (!selectionPromptLabel.texture) {
    selectionPromptLabel.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, selectionPromptLabel.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, selectionPromptLabel.texture);
  }
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, selectionPromptLabel.canvas);
  selectionPromptLabel.width = selectionPromptLabel.canvas.width;
  selectionPromptLabel.height = selectionPromptLabel.canvas.height;
  selectionPromptLabel.sourceWidth = selectionPromptLabel.canvas.width;
  selectionPromptLabel.sourceHeight = selectionPromptLabel.canvas.height;
}

function getSelectionPromptMessage() {
  return languageState.current === "en"
    ? "Select a piece to move."
    : "\uc6c0\uc9c1\uc77c \ub9d0\uc744 \uc120\ud0dd\ud574\uc8fc\uc138\uc694.";
}

function updateSidePanelLabels() {
  updateSeasonPanelLabelTexture();
  updateLuckyPanelLabelTexture();
  updateBluePanelLabelTexture();
}

function getSeasonTexts() {
  if (languageState.current === "en") {
    return { title: SEASON_TITLE_TEXT_EN, time: getSeasonTimeText("en") };
  }
  return { title: SEASON_TITLE_TEXT_KO, time: getSeasonTimeText("ko") };
}

function updateSeasonPanelLabelTexture() {
  if (!seasonPanelLabel.ctx) {
    return;
  }
  const ctx = seasonPanelLabel.ctx;
  const width = seasonPanelLabel.canvas.width;
  const height = seasonPanelLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#4f473b";
  drawRoundedRect(ctx, 0, 0, width, height, height * 0.12);
  ctx.fill();

  const padX = width * SIDE_PANEL_PAD_RATIO;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#f2d5a0";
  const seasonFontSize = Math.round(height * 0.26);
  ctx.font = `800 ${seasonFontSize}px NanumSquareB`;
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = height * 0.04;
  ctx.shadowOffsetY = height * 0.04;
  const seasonTexts = getSeasonTexts();
  ctx.fillText(seasonTexts.title, padX, height * 0.16);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(seasonTexts.time, padX, height * 0.6);
  ctx.shadowColor = "rgba(0, 0, 0, 0)";

  uploadLabelTexture(seasonPanelLabel);
}

function isSeasonEnded() {
  return !!seasonEndTime && Date.now() >= seasonEndTime.getTime();
}

function applyEventEndFromServer(value) {
  if (!value) {
    return;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return;
  }
  // Server-provided event end takes priority over local/debug values.
  seasonEndTime = parsed;
  lastSeasonMinutes = null;
  seasonEnded = false;
  seasonState.ended = false;
  seasonEndFromServer = true;
  updateSeasonPanelLabelTexture();
  requestDraw();
}

function getSeasonTimeText(lang) {
  if (!seasonEndTime || Number.isNaN(seasonEndTime.getTime())) {
    return lang === "en" ? SEASON_TIME_TEXT_EN : SEASON_TIME_TEXT_KO;
  }
  const now = new Date();
  const minutesTotal = Math.max(
    0,
    Math.floor((seasonEndTime.getTime() - now.getTime()) / 60000)
  );
  const days = Math.floor(minutesTotal / 1440);
  const hours = Math.floor((minutesTotal % 1440) / 60);
  const minutes = minutesTotal % 60;
  if (lang === "en") {
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m left`;
    }
    return `${hours}h ${minutes}m left`;
  }
  if (days > 0) {
    return `${days}일 ${hours}시간 ${minutes}분 남음`;
  }
  return `${hours}시간 ${minutes}분 남음`;
}

function parseDatetimeLocal(value) {
  if (!value) {
    return null;
  }
  const parts = value.split("T");
  if (parts.length < 2) {
    return null;
  }
  const dateParts = parts[0].split("-").map((v) => Number(v));
  const timeParts = parts[1].split(":").map((v) => Number(v));
  if (dateParts.length < 3 || timeParts.length < 2) {
    return null;
  }
  const [year, month, day] = dateParts;
  const [hour, minute] = timeParts;
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }
  return new Date(year, month - 1, day, hour, minute, 0, 0);
}

function updateLuckyPanelLabelTexture() {
  if (!luckyPanelLabel.ctx) {
    return;
  }
  const ctx = luckyPanelLabel.ctx;
  const width = luckyPanelLabel.canvas.width;
  const height = luckyPanelLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#4f473b";
  drawRoundedRect(ctx, 0, 0, width, height, height * 0.12);
  ctx.fill();

  const padX = width * SIDE_PANEL_PAD_RATIO;
  const iconSlot = width * 0.19;
  const giftSlot = width * LUCKY_PANEL_GIFT_RATIO;
  const textX = padX + iconSlot;
  const textWidth = width - padX - giftSlot - textX;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 ${Math.round(height * 0.3)}px NanumSquareB`;
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = height * 0.04;
  ctx.shadowOffsetY = height * 0.04;
  ctx.fillText(formatScore(gameState.score), textX, height * 0.52, textWidth);
  ctx.shadowColor = "rgba(0, 0, 0, 0)";

  const giftRect = {
    x: width - giftSlot,
    y: 0,
    w: giftSlot,
    h: height,
  };
  ctx.fillStyle = "#5b4f3f";
  drawRoundedRect(ctx, giftRect.x, giftRect.y, giftRect.w, giftRect.h, height * LUCKY_PANEL_GIFT_RADIUS);
  ctx.fill();

  uploadLabelTexture(luckyPanelLabel);
}

function updateBluePanelLabelTexture() {
  if (!bluePanelLabel.ctx) {
    return;
  }
  const ctx = bluePanelLabel.ctx;
  const width = bluePanelLabel.canvas.width;
  const height = bluePanelLabel.canvas.height;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#4f473b";
  drawRoundedRect(ctx, 0, 0, width, height, height * 0.12);
  ctx.fill();

  const padX = width * SIDE_PANEL_PAD_RATIO;
  const iconSlot = width * 0.19;
  const giftSlot = width * LUCKY_PANEL_GIFT_RATIO;
  const textX = padX + iconSlot;
  const textWidth = width - padX - giftSlot - textX;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 ${Math.round(height * 0.3)}px NanumSquareB`;
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = height * 0.04;
  ctx.shadowOffsetY = height * 0.04;
  ctx.fillText(formatScore(gameState.blueScore), textX, height * 0.52, textWidth);
  ctx.shadowColor = "rgba(0, 0, 0, 0)";

  const giftRect = {
    x: width - giftSlot,
    y: 0,
    w: giftSlot,
    h: height,
  };
  ctx.fillStyle = "#5b4f3f";
  drawRoundedRect(ctx, giftRect.x, giftRect.y, giftRect.w, giftRect.h, height * LUCKY_PANEL_GIFT_RADIUS);
  ctx.fill();

  uploadLabelTexture(bluePanelLabel);
}

function drawMixedColorText(ctx, text, x, y, numberColor, textColor) {
  const parts = text.match(/\d+|[^\d]+/g) || [text];
  let cursor = x;
  for (const part of parts) {
    const isNumber = /^\d+$/.test(part);
    ctx.fillStyle = isNumber ? numberColor : textColor;
    ctx.fillText(part, cursor, y);
    cursor += ctx.measureText(part).width;
  }
}

function formatScore(value) {
  const num = Number.isFinite(Number(value)) ? Number(value) : 0;
  return Math.max(0, Math.round(num)).toLocaleString("en-US");
}

function uploadLabelTexture(label) {
  if (!label.canvas) {
    return;
  }
  if (!label.texture) {
    label.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, label.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, label.texture);
  }
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, label.canvas);
  label.width = label.canvas.width;
  label.height = label.canvas.height;
  label.sourceWidth = label.canvas.width;
  label.sourceHeight = label.canvas.height;
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width * 0.5, height * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function offsetRect(rect, offset) {
  return {
    left: rect.left + offset.x,
    bottom: rect.bottom + offset.y,
    width: rect.width,
    height: rect.height,
  };
}

function updateTitleState(dt) {
  if (titleState.phase === "loading" || titleState.phase === "corner") {
    return;
  }
  titleState.elapsed += dt;
  titleState.floatTime += dt;
  if (titleState.phase === "fadein" && titleState.elapsed >= TITLE_FADEIN_DURATION) {
    titleState.phase = "idle";
    titleState.elapsed = 0;
  } else if (titleState.phase === "toCorner" && titleState.elapsed >= TITLE_DOCK_DURATION) {
    titleState.phase = "corner";
    titleState.elapsed = 0;
  }
}

function startTitleDock() {
  if (titleState.phase !== "idle") {
    return;
  }
  titleState.phase = "toCorner";
  titleState.elapsed = 0;
}

function getBackgroundAlpha() {
  if (titleState.phase === "loading") {
    return 0;
  }
  if (titleState.phase === "fadein") {
    return Math.min(1, titleState.elapsed / TITLE_FADEIN_DURATION);
  }
  return 1;
}

function getTitleAlpha() {
  if (titleState.phase === "loading") {
    return 0;
  }
  if (titleState.phase === "fadein") {
    return Math.min(1, titleState.elapsed / TITLE_APPEAR_DURATION);
  }
  return 1;
}

function getTitleLayout(panelRect) {
  if (!titleSprite.texture) {
    return null;
  }
  const alpha = getTitleAlpha();
  if (alpha <= 0) {
    return null;
  }
  const spriteSize = getSpriteSourceSize(titleSprite);
  const baseHeight = panelRect.height * TITLE_HEIGHT_RATIO;
  const baseWidth = baseHeight * (spriteSize.width / spriteSize.height);
  const floatOffset =
    titleState.phase === "idle" || titleState.phase === "fadein"
      ? Math.sin(titleState.floatTime * TITLE_FLOAT_SPEED) * (panelRect.height * 0.012)
      : 0;
  const baseCenter = {
    x: panelRect.left + panelRect.width * 0.5,
    y: panelRect.bottom + panelRect.height * 0.5 + floatOffset,
  };
  const cornerHeight = baseHeight * TITLE_CORNER_SCALE;
  const cornerWidth = cornerHeight * (spriteSize.width / spriteSize.height);
  const margin = Math.min(panelRect.width, panelRect.height) * TITLE_CORNER_MARGIN;
  const cornerCenter = {
    x: panelRect.left + margin + cornerWidth * 0.5,
    y: panelRect.bottom + panelRect.height - margin - cornerHeight * 0.5 + panelRect.height * TITLE_CORNER_OFFSET_Y,
  };
  let progress = 0;
  if (titleState.phase === "toCorner") {
    const t = Math.min(1, titleState.elapsed / TITLE_DOCK_DURATION);
    progress = 1 - Math.pow(1 - t, 3);
  } else if (titleState.phase === "corner") {
    progress = 1;
  }
  const height = baseHeight + (cornerHeight - baseHeight) * progress;
  const widthScale =
    languageState.current === "en"
      ? 1 - (1 - TITLE_EN_WIDTH_SCALE) * progress
      : 1;
  const width = (baseWidth + (cornerWidth - baseWidth) * progress) * widthScale;
  const center = {
    x: baseCenter.x + (cornerCenter.x - baseCenter.x) * progress,
    y: baseCenter.y + (cornerCenter.y - baseCenter.y) * progress,
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  return { rect, alpha };
}

function renderTitle(panelRect) {
  const layout = getTitleLayout(panelRect);
  if (!layout) {
    return;
  }
  drawSpriteRect(titleSprite, layout.rect, [1, 1, 1, layout.alpha]);
}

function renderTitleHelp(panelRect) {
  if (titleState.phase !== "corner" || !titleHelpSprite.texture) {
    return;
  }
  const rect = getTitleHelpRect(panelRect);
  if (!rect) {
    return;
  }
  drawSpriteRect(titleHelpSprite, rect, [1, 1, 1, 1]);
}

function getSidePanelRects(panelRect) {
  if (titleState.phase !== "corner") {
    return null;
  }
  const layout = getTitleLayout(panelRect);
  if (!layout) {
    return null;
  }
  const titleRect = layout.rect;
  const width = panelRect.width * SIDE_PANEL_WIDTH_RATIO;
  const stretch = panelRect.width * SIDE_PANEL_WIDTH_EXTEND_RATIO;
  const seasonHeight = panelRect.height * SEASON_PANEL_HEIGHT_RATIO;
  const luckyHeight = panelRect.height * LUCKY_PANEL_HEIGHT_RATIO;
  const gap = panelRect.height * SIDE_PANEL_GAP_RATIO;
  const top = titleRect.bottom - panelRect.height * SIDE_PANEL_OFFSET_RATIO;
  const seasonRect = {
    left: titleRect.left,
    bottom: top - seasonHeight,
    width: width + stretch,
    height: seasonHeight,
  };
  const luckyRect = {
    left: titleRect.left,
    bottom: seasonRect.bottom - gap - luckyHeight,
    width: width + stretch,
    height: luckyHeight,
  };
  const blueRect = {
    left: titleRect.left,
    bottom: luckyRect.bottom - gap - luckyHeight,
    width: width + stretch,
    height: luckyHeight,
  };
  return { seasonRect, luckyRect, blueRect };
}

function getBagIconRect(luckyRect, sprite, forcedVisibleHeight = null) {
  const padX = luckyRect.width * SIDE_PANEL_PAD_RATIO;
  const slotWidth = luckyRect.width * LUCKY_PANEL_ICON_SLOT_RATIO;
  const targetVisibleHeight = forcedVisibleHeight ?? luckyRect.height * 0.6;
  let iconWidth = targetVisibleHeight;
  let iconHeight = targetVisibleHeight;
  let visibleHeight = targetVisibleHeight;
  if (sprite && sprite.texture) {
    const spriteSize = getSpriteSourceSize(sprite);
    const sourceWidth = spriteSize.width || 1;
    const sourceHeight = spriteSize.height || 1;
    const trimWidth = sprite.trimmed && sprite.trimWidth ? sprite.trimWidth : sourceWidth;
    const trimHeight = sprite.trimmed && sprite.trimHeight ? sprite.trimHeight : sourceHeight;
    iconHeight = targetVisibleHeight * (sourceHeight / trimHeight);
    iconWidth = targetVisibleHeight * (sourceWidth / trimHeight);
  }
  if (iconWidth > slotWidth) {
    const scale = slotWidth / iconWidth;
    iconWidth *= scale;
    iconHeight *= scale;
    visibleHeight *= scale;
  }
  return {
    rect: {
      left: luckyRect.left + padX,
      bottom: luckyRect.bottom + (luckyRect.height - iconHeight) * 0.5,
      width: iconWidth,
      height: iconHeight,
    },
    visibleHeight,
  };
}

function getLuckyBagIconRect(luckyRect) {
  return getBagIconRect(luckyRect, luckyBagSprite);
}

function getGiftIconRect(luckyRect) {
  const iconSize = getSpriteSourceSize(giftIconSprite);
  const giftSlot = luckyRect.width * LUCKY_PANEL_GIFT_RATIO;
  const targetHeight = luckyRect.height * 0.55;
  let iconWidth = targetHeight * (iconSize.width / iconSize.height);
  let iconHeight = targetHeight;
  const maxWidth = giftSlot * 0.5;
  if (iconWidth > maxWidth) {
    iconWidth = maxWidth;
    iconHeight = iconWidth * (iconSize.height / iconSize.width);
  }
  return {
    left: luckyRect.left + luckyRect.width - giftSlot + (giftSlot - iconWidth) * 0.5,
    bottom: luckyRect.bottom + (luckyRect.height - iconHeight) * 0.5,
    width: iconWidth,
    height: iconHeight,
  };
}

function getGiftButtonRect(luckyRect) {
  const giftSlot = luckyRect.width * LUCKY_PANEL_GIFT_RATIO;
  return {
    left: luckyRect.left + luckyRect.width - giftSlot,
    bottom: luckyRect.bottom,
    width: giftSlot,
    height: luckyRect.height,
  };
}

function getVisibleIconLeft(rect, sprite) {
  if (!sprite) {
    return rect.left;
  }
  const sourceWidth = sprite.sourceWidth || sprite.width || rect.width;
  const trimX = sprite.trimX || 0;
  return rect.left + (trimX / sourceWidth) * rect.width;
}

function alignIconRectToVisibleLeft(rect, sprite, targetVisibleLeft) {
  if (!sprite) {
    rect.left = targetVisibleLeft;
    return;
  }
  const sourceWidth = sprite.sourceWidth || sprite.width || rect.width;
  const trimX = sprite.trimX || 0;
  rect.left = targetVisibleLeft - (trimX / sourceWidth) * rect.width;
}

function getLuckyBagTarget(panelRect) {
  const rects = getSidePanelRects(panelRect);
  if (!rects) {
    return null;
  }
  const iconRect = getLuckyBagIconRect(rects.luckyRect).rect;
  return {
    x: iconRect.left + iconRect.width * 0.5,
    y: iconRect.bottom + iconRect.height * 0.5,
  };
}

function getBlueBagTarget(panelRect) {
  const rects = getSidePanelRects(panelRect);
  if (!rects) {
    return null;
  }
  const redInfo = getLuckyBagIconRect(rects.luckyRect);
  const blueInfo = getBagIconRect(rects.blueRect, luckyBagBlueSprite, redInfo.visibleHeight);
  const redVisibleLeft = getVisibleIconLeft(redInfo.rect, luckyBagSprite);
  alignIconRectToVisibleLeft(blueInfo.rect, luckyBagBlueSprite, redVisibleLeft);
  const iconRect = blueInfo.rect;
  return {
    x: iconRect.left + iconRect.width * 0.5,
    y: iconRect.bottom + iconRect.height * 0.5,
  };
}

function getMissionButtonRect(blueRect) {
  const size = getSpriteSourceSize(missionButtonSprite);
  const finalWidth = size.width || 0;
  const finalHeight = size.height || 0;
  if (!finalWidth || !finalHeight) {
    return {
      left: blueRect.left,
      bottom: blueRect.bottom,
      width: 0,
      height: 0,
    };
  }
  const center = {
    x: blueRect.left + finalWidth * 0.5,
    y: blueRect.bottom - finalHeight * 0.5 - blueRect.height * 0.1,
  };
  return {
    left: center.x - finalWidth * 0.5,
    bottom: center.y - finalHeight * 0.5,
    width: finalWidth,
    height: finalHeight,
  };
}

function getPressedRect(rect, scale = 0.94) {
  const width = rect.width * scale;
  const height = rect.height * scale;
  return {
    left: rect.left + (rect.width - width) * 0.5,
    bottom: rect.bottom + (rect.height - height) * 0.5,
    width,
    height,
  };
}

function getTitleHelpRect(panelRect) {
  const layout = getTitleLayout(panelRect);
  if (!layout) {
    return null;
  }
  const titleRect = layout.rect;
  const spriteSize = getSpriteSourceSize(titleHelpSprite);
  const height = titleRect.height * TITLE_HELP_SCALE;
  const width = height * (spriteSize.width / spriteSize.height);
  const gapScale = languageState.current === "en" ? 0.35 : 1;
  const gap = titleRect.width * TITLE_HELP_GAP * gapScale;
  const center = {
    x: titleRect.left + titleRect.width + gap + width * 0.5,
    y: titleRect.bottom + titleRect.height * 0.5,
  };
  return {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
}

function renderSidePanels(panelRect) {
  const rects = getSidePanelRects(panelRect);
  if (!rects || !seasonPanelLabel.texture || !luckyPanelLabel.texture || !bluePanelLabel.texture) {
    return;
  }
  const { seasonRect, luckyRect, blueRect } = rects;
  ensureLabelSize(seasonPanelLabel, seasonRect.width, seasonRect.height, updateSeasonPanelLabelTexture);
  ensureLabelSize(luckyPanelLabel, luckyRect.width, luckyRect.height, updateLuckyPanelLabelTexture);
  ensureLabelSize(bluePanelLabel, blueRect.width, blueRect.height, updateBluePanelLabelTexture);
  drawSpriteRect(seasonPanelLabel, seasonRect, [1, 1, 1, 1]);
  drawSpriteRect(luckyPanelLabel, luckyRect, [1, 1, 1, 1]);
  drawSpriteRect(bluePanelLabel, blueRect, [1, 1, 1, 1]);

  if (luckyBagSprite.texture) {
    const redInfo = getLuckyBagIconRect(luckyRect);
    drawSpriteRect(luckyBagSprite, redInfo.rect, [1, 1, 1, 1]);
    if (luckyBagBlueSprite.texture) {
      const blueInfo = getBagIconRect(blueRect, luckyBagBlueSprite, redInfo.visibleHeight);
      const redVisibleLeft = getVisibleIconLeft(redInfo.rect, luckyBagSprite);
      alignIconRectToVisibleLeft(blueInfo.rect, luckyBagBlueSprite, redVisibleLeft);
      drawSpriteRect(luckyBagBlueSprite, blueInfo.rect, [1, 1, 1, 1]);
    }
  }

  if (giftIconSprite.texture) {
    const redGiftRect = getGiftIconRect(luckyRect);
    const rect = redGiftPressed ? getPressedRect(redGiftRect) : redGiftRect;
    drawSpriteRect(giftIconSprite, rect, [1, 1, 1, redGiftPressed ? 0.85 : 1]);
  }
  if (blueGiftIconSprite.texture) {
    const blueGiftRect = getGiftIconRect(blueRect);
    const rect = blueGiftPressed ? getPressedRect(blueGiftRect) : blueGiftRect;
    drawSpriteRect(blueGiftIconSprite, rect, [1, 1, 1, blueGiftPressed ? 0.85 : 1]);
  }
  if (missionButtonSprite.texture) {
    const missionRect = getMissionButtonRect(blueRect);
    if (missionButtonPressed) {
      const pressedRect = {
        left: missionRect.left + missionRect.width * 0.02,
        bottom: missionRect.bottom + missionRect.height * 0.02,
        width: missionRect.width * 0.96,
        height: missionRect.height * 0.96,
      };
      drawSpriteRect(missionButtonSprite, pressedRect, [1, 1, 1, 0.85]);
    } else {
      drawSpriteRect(missionButtonSprite, missionRect, [1, 1, 1, 1]);
    }
  }
}

function ensureLabelSize(label, width, height, updateFn) {
  if (!label.canvas) {
    return;
  }
  const targetWidth = Math.max(1, Math.round(width));
  const targetHeight = Math.max(1, Math.round(height));
  if (label.canvas.width === targetWidth && label.canvas.height === targetHeight) {
    return;
  }
  label.canvas.width = targetWidth;
  label.canvas.height = targetHeight;
  label.ctx = label.canvas.getContext("2d");
  updateFn();
}

function renderLoadingSpinner(panelRect, timestamp) {
  if (!loadingSpinner.texture) {
    return;
  }
  const size = Math.min(panelRect.width, panelRect.height) * LOADING_SPINNER_RATIO;
  const baseSize = size;
  const center = {
    x: panelRect.left + panelRect.width * 0.5,
    y: panelRect.bottom + panelRect.height * 0.5,
  };
  const spin = (timestamp || performance.now()) * 0.003;
  drawSpriteRotated(loadingSpinner, center, baseSize, 1, 1, spin);
}

function renderSelectionPrompt(panelRect, alpha) {
  if (!selectionPromptSprite.texture || !selectionPromptLabel.texture) {
    return;
  }
  const spriteSize = getSpriteSourceSize(selectionPromptSprite);
  const height = panelRect.height * SELECTION_PROMPT_HEIGHT;
  const width = height * (spriteSize.width / spriteSize.height);
  const offset = (1 - alpha) * panelRect.height * 0.03;
  const center = {
    x: panelRect.left + panelRect.width * 0.5,
    y: panelRect.bottom + panelRect.height * 0.5 + offset,
  };
  const rect = {
    left: center.x - width * 0.5,
    bottom: center.y - height * 0.5,
    width,
    height,
  };
  drawSpriteRect(selectionPromptSprite, rect, [1, 1, 1, alpha]);

  const textSize = getSpriteSourceSize(selectionPromptLabel);
  const textHeight = height * SELECTION_PROMPT_TEXT_HEIGHT;
  const textWidth = textHeight * (textSize.width / textSize.height);
  const textRect = {
    left: center.x - textWidth * 0.5,
    bottom: center.y - textHeight * 0.5,
    width: textWidth,
    height: textHeight,
  };
  drawSpriteRect(selectionPromptLabel, textRect, [1, 1, 1, alpha]);
}

function isPromptUiReady() {
  if (titleState.phase !== "corner") {
    return false;
  }
  if (!boardAnim.initialized || boardAnim.active) {
    return false;
  }
  if (rosterState.spawnPending || isPiecesAnimating()) {
    return false;
  }
  return true;
}

function render(timestamp) {
  if (typeof timestamp === "number" && lastRenderTimestamp) {
    const since = timestamp - lastRenderTimestamp;
    const threshold = MAX_FRAME_INTERVAL_MS * FRAME_INTERVAL_TOLERANCE;
    if (since > 0 && since < threshold) {
      renderScheduled = true;
      requestAnimationFrame(render);
      return;
    }
  }
  renderScheduled = false;
  if (isRendering) {
    return;
  }
  isRendering = true;
  if (typeof timestamp === "number") {
    lastRenderTimestamp = timestamp;
  }
  needsRedraw = false;
  syncWinOverlayUi();
  if (turnState.freeThrowFor !== lastFreeThrowFor) {
    lastFreeThrowFor = turnState.freeThrowFor;
    updateYutThrowUI();
  }
  resizeCanvasToDisplaySize();
  const hasTimestamp = typeof timestamp === "number";
  const assetsReady = !!textures.board.texture;
  if (titleState.phase === "loading" && assetsReady) {
    titleState.phase = "fadein";
    titleState.elapsed = 0;
    titleState.floatTime = 0;
  }
  const showBoard = titleState.phase === "toCorner" || titleState.phase === "corner";
  const showGameplay = titleState.phase === "corner";
  const promptUiReady = showGameplay && isPromptUiReady();
  const initialBannerReady =
    showGameplay &&
    initialTurnBannerPending &&
    boardAnim.initialized &&
    !boardAnim.active &&
    !yutButtonAnim.pending &&
    !yutButtonAnim.active &&
    yutButtonAnim.progress >= 1 &&
    !rosterState.spawnPending &&
    !isPiecesAnimating() &&
    turnBannerSprites.player.texture;
  if (initialBannerReady && initialTurnDelayRemaining == null) {
    initialTurnDelayRemaining = 1.0;
  }
  const baseAnimating =
    boardAnim.active ||
    moveState.active ||
    isYutAnimating() ||
    isYutButtonAnimating() ||
    isYutResultOverlayAnimating() ||
    isOneMoreOverlayAnimating() ||
    isPiecesAnimating() ||
    isTurnBannerAnimating() ||
    selectionState.active ||
    isWinOverlayActive() ||
    initialTurnDelayRemaining != null ||
    separationState.animating ||
    shakeState.active ||
    scoreBags.length > 0 ||
    scoreBursts.length > 0 ||
    scoreEvents.length > 0 ||
    selectionPromptAlpha > 0.01 ||
    titleState.phase !== "corner";
  const idleBreatheActive = showGameplay && !baseAnimating;
  const wantsAnimation = baseAnimating || idleBreatheActive;
  let dt = 0;
  if (wantsAnimation && hasTimestamp) {
    if (!lastFrameTime) {
      lastFrameTime = timestamp;
    }
    dt = Math.min((timestamp - lastFrameTime) / 1000, 0.05);
  }

  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  const panelRect = computePanelRect(canvas.width, canvas.height);
  if (!assetsReady) {
    renderLoadingSpinner(panelRect, timestamp);
    lastFrameTime = timestamp;
    lastRenderTimestamp = timestamp;
    renderScheduled = true;
    requestAnimationFrame(render);
    isRendering = false;
    return;
  }
  updateDebugFps(timestamp);
  beginSpriteBatch();
  if (dt > 0) {
    updateTitleState(dt);
  }

  if (showGameplay && dt > 0) {
    const target = promptUiReady && selectionState.active ? 1 : 0;
    selectionPromptAlpha = approach(selectionPromptAlpha, target, dt, 6);
  } else if (!showGameplay) {
    selectionPromptAlpha = 0;
  }

  if (textures.background.texture) {
    const backgroundAlpha = getBackgroundAlpha();
    const baseUvs = computeCoverUVs(
      textures.backgroundSize.width,
      textures.backgroundSize.height,
      canvas.width,
      canvas.height
    );
    const uvs = mapUvsToSprite(baseUvs, textures.background);
    drawQuad(
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      uvs,
      textures.background.texture,
      [1, 1, 1, backgroundAlpha]
    );
  }
  if (showGameplay && dt > 0) {
    updateShake(dt);
    updateMoveState(dt, panelRect);
    updatePieces(dt, panelRect);
    updateCharacterSeparation(dt, panelRect);
    updateWinOverlay(dt);
    maybeResumeAfterFinish();
  }
  if (showGameplay && seasonEndTime) {
    const remainingMs = seasonEndTime.getTime() - Date.now();
    const minutes = Math.max(0, Math.floor(remainingMs / 60000));
    if (minutes !== lastSeasonMinutes) {
      lastSeasonMinutes = minutes;
      updateSeasonPanelLabelTexture();
    }
    if (!seasonEnded && remainingMs <= 0) {
      seasonEnded = true;
      seasonState.ended = true;
      setAutoPlayEnabled(false);
      updateYutThrowUI();
    } else if (seasonEnded && remainingMs > 0) {
      seasonEnded = false;
      seasonState.ended = false;
      updateYutThrowUI();
    }
  } else if (seasonEnded && !seasonEndTime) {
    seasonEnded = false;
    seasonState.ended = false;
    updateYutThrowUI();
  }
  const luckyTarget = showGameplay ? getLuckyBagTarget(panelRect) : null;
  const blueTarget = showGameplay ? getBlueBagTarget(panelRect) : null;
  const luckyTargetNormalized = luckyTarget ? pixelToNormalized(luckyTarget, panelRect) : null;
  const blueTargetNormalized = blueTarget ? pixelToNormalized(blueTarget, panelRect) : null;
  if (showGameplay && dt > 0) {
    updateScoreBags(dt, panelRect, { red: luckyTargetNormalized, blue: blueTargetNormalized });
  }
  const shakeOffset = showGameplay ? getShakeOffset() : { x: 0, y: 0 };
  const renderPanelRect = offsetRect(panelRect, shakeOffset);
  lastRenderPanelRect = renderPanelRect;
  updateDebugYutButtonsPosition(renderPanelRect);
  if (winOverlayUiActive && winRestartLayoutDirty) {
    updateWinRestartAtlas();
  }
  if (guideOverlayEnabled) {
    const panelQuad = rectToNdcQuad(renderPanelRect, canvas.width, canvas.height);
    drawQuad(panelQuad, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), whiteTexture, [
      0,
      0,
      0,
      0.25,
    ]);
  }

  let boardRectForYut = renderPanelRect;
  let logicalBoardRect = null;
  if (showBoard && textures.board.texture) {
    const finalBoardRect = computeContainedRect(panelRect, textures.boardSize, 0.9);

    if (!boardAnim.initialized) {
      resetBoardAnim(panelRect, finalBoardRect);
    } else {
      boardAnim.targetY = finalBoardRect.bottom;
      if (!boardAnim.active) {
        boardAnim.y = boardAnim.targetY;
      }
    }

    if (boardAnim.active && dt > 0) {
      updateBoardAnim(dt);
    }
    if (showGameplay) {
      maybeStartPendingRosterSpawn(panelRect);
    }

    const boardRect = {
      left: finalBoardRect.left,
      bottom: boardAnim.active ? boardAnim.y : finalBoardRect.bottom,
      width: finalBoardRect.width,
      height: finalBoardRect.height,
    };
    logicalBoardRect = boardRect;
    const renderBoardRect = offsetRect(boardRect, shakeOffset);

    drawSpriteRect(textures.board, renderBoardRect, [1, 1, 1, 1]);
    boardRectForYut = renderBoardRect;
  }

  if (showGameplay) {
    if (isYutAnimating() && dt > 0) {
      updateYutAnimation(dt, panelRect);
    }

    if (isYutResultOverlayAnimating() && dt > 0) {
      updateYutResultOverlay(dt);
    }

    if (isOneMoreOverlayAnimating() && dt > 0) {
      updateOneMoreOverlay(dt);
    }

    if (isTurnBannerAnimating() && dt > 0) {
      updateTurnBanner(dt);
    }
    if (initialTurnDelayRemaining != null && dt > 0) {
      initialTurnDelayRemaining -= dt;
      if (initialTurnDelayRemaining <= 0) {
        initialTurnDelayRemaining = null;
        initialTurnBannerPending = false;
        startTurnBanner("player");
      }
    }

    if (isYutButtonAnimating() && dt > 0) {
      updateYutButtonAnimation(dt);
    }

    if (yutButtonAnim.pending && boardAnim.initialized && !boardAnim.active) {
      startYutButtonDrop();
    }

    maybeAutoThrow();
    maybeAutoSelect();

    renderYutButton(renderPanelRect);
    renderPieces(renderPanelRect, logicalBoardRect || renderPanelRect);
    if (promptUiReady) {
      renderSelectionArrows(renderPanelRect, logicalBoardRect || renderPanelRect);
      renderBranchArrows(renderPanelRect);
    }
    renderAutoToggle(renderPanelRect);

    renderYutAnimation(boardRectForYut);
    renderYutResultOverlay(boardRectForYut);
    renderOneMoreOverlay(boardRectForYut);
    renderTurnBanner(renderPanelRect);

    if (promptUiReady && selectionPromptAlpha > 0.01) {
      renderSelectionPrompt(renderPanelRect, selectionPromptAlpha);
    }
    renderWinOverlay(renderPanelRect);
  }

  if (gameState.score !== lastScore) {
    lastScore = gameState.score;
    updateLuckyPanelLabelTexture();
    syncMockDebugInputs();
  }
  if (gameState.blueScore !== lastBlueScore) {
    lastBlueScore = gameState.blueScore;
    updateBluePanelLabelTexture();
    syncMockDebugInputs();
  }

  if (titleState.phase !== "loading") {
    renderTitle(renderPanelRect);
    renderTitleHelp(renderPanelRect);
    renderSidePanels(renderPanelRect);
    renderScoreBags(renderPanelRect);
  }

  if (editor.enabled) {
    renderEditorOverlay(renderPanelRect);
  }

  endSpriteBatch();

  if (baseAnimating) {
    cancelIdleBreathe();
    lastFrameTime = timestamp;
    lastRenderTimestamp = timestamp;
    renderScheduled = true;
    requestAnimationFrame(render);
  } else if (needsRedraw) {
    cancelIdleBreathe();
    lastFrameTime = timestamp;
    lastRenderTimestamp = timestamp;
    renderScheduled = true;
    requestAnimationFrame(render);
  } else if (idleBreatheActive) {
    lastFrameTime = timestamp;
    scheduleIdleBreathe();
  } else {
    cancelIdleBreathe();
    lastFrameTime = 0;
    lastRenderTimestamp = 0;
  }
  isRendering = false;
}

function resetBoardAnim(panelRect, finalBoardRect) {
  boardAnim.targetY = finalBoardRect.bottom;
  boardAnim.y = panelRect.bottom + panelRect.height + finalBoardRect.height * 1.2;
  boardAnim.vy = 0;
  boardAnim.active = true;
  boardAnim.initialized = true;
  lastFrameTime = 0;
}

function updateBoardAnim(dt) {
  const gravity = -4200;
  const bounce = 0.35;
  const settleVelocity = 60;

  boardAnim.vy += gravity * dt;
  boardAnim.y += boardAnim.vy * dt;

  if (boardAnim.y <= boardAnim.targetY) {
    boardAnim.y = boardAnim.targetY;
    boardAnim.vy = -boardAnim.vy * bounce;
    if (Math.abs(boardAnim.vy) < settleVelocity) {
      boardAnim.vy = 0;
      boardAnim.active = false;
    }
  }
}

function handleGamePointerDown(event) {
  if (event.button !== 0) {
    return;
  }
  if (titleState.phase !== "corner") {
    return;
  }
  const panelRect = getPanelRectForInput();
  const hitPanelRect = lastRenderPanelRect || panelRect;
  const point = getCanvasPoint(event);
  if (!pointInRect(point, hitPanelRect)) {
    return;
  }
  if (titleHelpSprite.texture) {
    const helpRect = getTitleHelpRect(hitPanelRect);
    if (helpRect && pointInRect(point, helpRect)) {
      helpPopupMode = "help";
      setHelpPopupActive(!helpPopupActive);
      return;
    }
  }
  const autoToggleRect = getAutoToggleRect(hitPanelRect);
  if (autoToggleRect && pointInRect(point, autoToggleRect)) {
    if (seasonEnded || isSeasonEnded()) {
      openSeasonEndAlert();
      return;
    }
    toggleAutoPlay();
    requestDraw();
    return;
  }
  if (runtimeState.actionBusy) {
    return;
  }
  if (seasonEnded || isSeasonEnded()) {
    openSeasonEndAlert();
    return;
  }
  const panelRects = getSidePanelRects(hitPanelRect);
  if (panelRects) {
    if (giftIconSprite.texture) {
      const redGiftRect = getGiftButtonRect(panelRects.luckyRect);
      if (pointInRect(point, redGiftRect)) {
        playSfx("button_click");
        redGiftPressed = true;
        requestDraw();
        setTimeout(() => {
          redGiftPressed = false;
          requestDraw();
        }, 120);
        onClickRewards();
        return;
      }
    }
    if (blueGiftIconSprite.texture) {
      const blueGiftRect = getGiftButtonRect(panelRects.blueRect);
      if (pointInRect(point, blueGiftRect)) {
        playSfx("button_click");
        blueGiftPressed = true;
        requestDraw();
        setTimeout(() => {
          blueGiftPressed = false;
          requestDraw();
        }, 120);
        onClickShop();
        return;
      }
    }
  }
  if (missionButtonSprite.texture) {
    if (panelRects) {
      const missionRect = getMissionButtonRect(panelRects.blueRect);
      if (pointInRect(point, missionRect)) {
        playSfx("button_click");
        missionButtonPressed = true;
        requestDraw();
        setTimeout(() => {
          missionButtonPressed = false;
          requestDraw();
        }, 120);
        onClickMissions();
        return;
      }
    }
  }
  if (!isPromptUiReady()) {
    return;
  }
  if (branchState.active) {
    const nextId = pickBranchChoiceAtPoint(point, panelRect);
    if (nextId != null) {
      playSfx("select_piece");
      clearBranchState();
      requestServerAction({ type: "select_branch", next_id: nextId });
      return;
    }
    return;
  }
  if (selectionState.active) {
    const group = pickSelectableGroupAtPoint(point, panelRect);
    if (group) {
      playSfx("select_piece");
      selectionState.active = false;
      selectionState.steps = 0;
      selectionState.groups = [];
      requestServerAction({ type: "select_move", group_id: group.id });
      return;
    }
    return;
  }
  if (yutButtonTexture.texture) {
    const buttonRect = getYutButtonRect(hitPanelRect);
    if (pointInRect(point, buttonRect)) {
      if (seasonEnded || isSeasonEnded()) {
        openSeasonEndAlert();
        return;
      }
      if (!isPlayerThrowReady()) {
        return;
      }
      playSfx("button_click");
      requestServerAction({ type: "throw" });
    }
  }
}

function onPointerDown(event) {
  if (isTypingInInput()) {
    return;
  }
  ensureSfxUnlocked();
  if (titleState.phase !== "corner") {
    if (titleState.phase === "idle" && event.button === 0) {
      startTitleDock();
      requestDraw();
    }
    return;
  }
  handleGamePointerDown(event);
}

function onPointerMove(event) {
  if (isTypingInInput()) {
    return;
  }
}

function onPointerUp(event) {
  if (isTypingInInput()) {
    return;
  }
}

const debugButton = document.getElementById("debug-toggle");
const debugFps = document.getElementById("debug-fps");
const guideButton = document.getElementById("guide-toggle");
const helpPopup = document.getElementById("help-popup");
const helpClose = document.getElementById("help-close");
const winOverlayUi = document.getElementById("win-overlay");
const winRestartButton = document.getElementById("win-restart");
const optionsUi = document.getElementById("options-ui");
const optionsToggle = document.getElementById("options-toggle");
const optionsPanel = document.getElementById("options-panel");
const optionsVolumeInput = document.getElementById("options-volume");
const optionsVolumeValue = document.getElementById("options-volume-value");
const optionsVolumeDown = document.getElementById("options-volume-down");
const optionsVolumeUp = document.getElementById("options-volume-up");
const optionsLanguageSelect = document.getElementById("options-language");
const optionsDebugRow = document.getElementById("options-debug-row");
const optionsDebugToggle = document.getElementById("options-debug-toggle");
const debugPanel = document.getElementById("debug-panel");
const sfxPanel = document.getElementById("sfx-panel");
const debugRestartBonusInput = document.getElementById("restart-bonus-throws");
const debugFixedYutInput = document.getElementById("debug-fixed-yut");
const debugYutButtons = document.getElementById("debug-yut-buttons");
const debugYutButtonInputs = Array.from(
  document.querySelectorAll("#debug-yut-buttons button[data-debug-yut]")
);
const debugSeasonEndInput = document.getElementById("debug-season-end");
const mockUserSection = document.getElementById("mock-user-controls");
const mockTicketInput = document.getElementById("mock-ticket-count");
const mockRedBagInput = document.getElementById("mock-red-bag");
const mockBlueBagInput = document.getElementById("mock-blue-bag");
const mockApplyButton = document.getElementById("mock-apply");
const mockResetButton = document.getElementById("mock-reset");
const sfxEnabledToggle = document.getElementById("sfx-enabled");
const sfxVolumeInput = document.getElementById("sfx-volume");
const sfxUploadButton = document.getElementById("sfx-upload-button");
const sfxUploadInput = document.getElementById("sfx-upload-input");
const sfxUploadTarget = document.getElementById("sfx-upload-target");
const sfxSelectInputs = Array.from(document.querySelectorAll("#sfx-panel select[data-sfx-select]"));
const sfxDeleteButtons = Array.from(document.querySelectorAll("#sfx-panel button[data-sfx-delete]"));
let winOverlayUiActive = false;
let optionsOpen = false;
let debugUiOpen = false;
let debugUiEnabled = true;
let winRestartLayoutDirty = true;

setDebugUiVisible(runtimeState.debugAllowed);

function syncWinOverlayUi() {
  if (!winOverlayUi) {
    return;
  }
  const active = isWinOverlayActive() && titleState.phase === "corner" && !!textures.board.texture;
  if (active === winOverlayUiActive) {
    return;
  }
  winOverlayUiActive = active;
  winOverlayUi.classList.toggle("is-active", active);
  winOverlayUi.setAttribute("aria-hidden", active ? "false" : "true");
  if (active) {
    requestWinRestartLayout();
  }
}

function setOptionsOpen(next) {
  optionsOpen = !!next;
  if (!optionsToggle || !optionsPanel) {
    return;
  }
  optionsToggle.setAttribute("aria-pressed", optionsOpen ? "true" : "false");
  optionsPanel.classList.toggle("is-active", optionsOpen);
  optionsPanel.setAttribute("aria-hidden", optionsOpen ? "false" : "true");
}

function setOptionsUiVisible(allowed) {
  if (!optionsUi) {
    return;
  }
  optionsUi.style.display = allowed ? "" : "none";
  if (!allowed) {
    setOptionsOpen(false);
  }
}

function setDebugPanelsOpen(next) {
  debugUiOpen = !!next;
  if (debugPanel) {
    debugPanel.classList.toggle("is-active", debugUiOpen);
    debugPanel.setAttribute("aria-hidden", debugUiOpen ? "false" : "true");
  }
  if (sfxPanel) {
    sfxPanel.classList.toggle("is-active", debugUiOpen);
    sfxPanel.setAttribute("aria-hidden", debugUiOpen ? "false" : "true");
  }
  if (debugButton) {
    debugButton.classList.toggle("is-active", debugUiOpen);
    debugButton.setAttribute("aria-pressed", debugUiOpen ? "true" : "false");
    debugButton.textContent = debugUiOpen ? "DEBUG ON" : "DEBUG";
  }
}
if (debugButton) {
  debugButton.addEventListener("click", () => {
    if (!runtimeState.debugAllowed) {
      return;
    }
    setDebugPanelsOpen(!debugUiOpen);
  });
}
if (guideButton) {
  syncGuideButtonUi();
  guideButton.addEventListener("click", () => {
    setGuideOverlayEnabled(!guideOverlayEnabled);
  });
}

if (winRestartButton) {
  winRestartButton.addEventListener("click", () => {
    setAutoPlayEnabled(false);
    requestServerAction({ type: "restart" });
  });
}

if (optionsToggle) {
  optionsToggle.addEventListener("click", () => {
    ensureSfxUnlocked();
    setOptionsOpen(!optionsOpen);
  });
}

if (optionsDebugToggle) {
  optionsDebugToggle.addEventListener("click", () => {
    if (!runtimeState.debugAllowed) {
      return;
    }
    debugUiEnabled = !debugUiEnabled;
    setDebugControlsVisible(debugUiEnabled);
    updateOptionsDebugToggle();
  });
}

window.addEventListener("pointerdown", (event) => {
  if (!optionsOpen) {
    return;
  }
  const target = event.target;
  if (optionsPanel && optionsPanel.contains(target)) {
    return;
  }
  if (optionsToggle && optionsToggle.contains(target)) {
    return;
  }
  setOptionsOpen(false);
});

function setHelpPopupActive(next) {
  helpPopupActive = next;
  if (!helpPopup) {
    return;
  }
  if (!helpPopupActive) {
    helpPopupMode = "help";
  }
  helpPopup.classList.toggle("is-active", helpPopupActive);
  helpPopup.setAttribute("aria-hidden", helpPopupActive ? "false" : "true");
  if (helpPopupActive) {
    if (helpPopupMode === "alert") {
      populateHelpFromMarkdown(getSeasonEndMessage(), "alert");
    } else {
      populateHelpFromMarkdown(getHelpMarkdown(), helpPopupMode);
    }
    updateHelpHeader();
  }
}

function openSeasonEndAlert() {
  helpPopupMode = "alert";
  setHelpPopupActive(true);
}

function getSeasonEndMessage() {
  return languageState.current === "en" ? "Season has ended." : "\uc2dc\uc98c\uc774 \uc885\ub8cc\ub418\uc5c8\uc2b5\ub2c8\ub2e4.";
}

if (helpClose) {
  helpClose.addEventListener("click", () => setHelpPopupActive(false));
}
if (helpPopup) {
  helpPopup.addEventListener("click", (event) => {
    if (event.target === helpPopup) {
      setHelpPopupActive(false);
    }
  });
}

function populateHelpFromMarkdown(markdown, mode = "help") {
  const body = document.querySelector(".help-body");
  if (!body) {
    return;
  }
  if (mode === "alert") {
    const message = markdown || "";
    body.innerHTML = `<div class="help-alert">${message}</div>`;
    return;
  }
  if (!markdown) {
    return;
  }
  body.innerHTML = renderSimpleMarkdown(markdown);
}

function getHelpMarkdown() {
  if (languageState.current === "en" && helpMarkdownTextEn) {
    return helpMarkdownTextEn;
  }
  return helpMarkdownTextKo || helpMarkdownTextEn || "";
}

function updateHelpHeader() {
  if (!helpPopup) {
    return;
  }
  const header = helpPopup.querySelector(".help-header");
  if (header) {
    const title =
      helpPopupMode === "alert"
        ? languageState.current === "en"
          ? "Alert"
          : "\uc54c\ub9bc"
        : languageState.current === "en"
          ? "Help"
          : "\ub3c4\uc6c0\ub9d0";
    header.firstChild.nodeValue = title;
  }
  helpPopup.setAttribute(
    "aria-label",
    helpPopupMode === "alert"
      ? languageState.current === "en"
        ? "Alert"
        : "\uc54c\ub9bc"
      : languageState.current === "en"
        ? "Help"
        : "\ub3c4\uc6c0\ub9d0"
  );
  const close = document.getElementById("help-close");
  if (close) {
    close.setAttribute(
      "aria-label",
      languageState.current === "en" ? "Close" : "\ub2eb\uae30"
    );
  }
}

function renderSimpleMarkdown(markdown) {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  let html = "";
  let inList = false;
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      continue;
    }
    if (line.startsWith("## ")) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += `<h3>${escapeHtml(line.slice(3))}</h3>`;
      continue;
    }
    if (line.startsWith("# ")) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += `<h2>${escapeHtml(line.slice(2))}</h2>`;
      continue;
    }
    if (line.startsWith("- ")) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      html += `<li>${escapeHtml(line.slice(2))}</li>`;
      continue;
    }
    if (inList) {
      html += "</ul>";
      inList = false;
    }
    html += `<p>${escapeHtml(line)}</p>`;
  }
  if (inList) {
    html += "</ul>";
  }
  return html;
}

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function isTypingInInput() {
  const el = document.activeElement;
  if (!el) {
    return false;
  }
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }
  return !!el.isContentEditable;
}

function initRestartBonusInput() {
  if (!debugRestartBonusInput) {
    return;
  }
  let value = 5;
  restartConfig.bonusThrows = Math.max(0, Math.round(value));
  debugRestartBonusInput.value = `${restartConfig.bonusThrows}`;
  const applyInput = () => {
    const next = Number(debugRestartBonusInput.value);
    restartConfig.bonusThrows = Number.isFinite(next) ? Math.max(0, Math.round(next)) : 0;
  };
  debugRestartBonusInput.addEventListener("input", applyInput);
  debugRestartBonusInput.addEventListener("change", applyInput);
}

function initDebugOptions() {
  if (!runtimeState.debugAllowed) {
    return;
  }
  if (debugFixedYutInput) {
    debugFixedYutInput.checked = debugOptions.fixedYut;
    debugFixedYutInput.addEventListener("change", () => {
      debugOptions.fixedYut = debugFixedYutInput.checked;
      if (!debugOptions.fixedYut) {
        debugOptions.forcedYutKey = null;
        updateDebugYutButtonsActive();
      }
      saveDebugOptions();
      updateDebugYutButtonsPosition(getPanelRectForInput());
    });
  }
  if (debugYutButtonInputs.length) {
    for (const button of debugYutButtonInputs) {
      button.addEventListener("click", () => {
        const key = button.dataset.debugYut;
        if (!key) {
          return;
        }
        debugOptions.forcedYutKey = key;
        updateDebugYutButtonsActive();
        saveDebugOptions();
      });
    }
    updateDebugYutButtonsActive();
  }
  if (debugSeasonEndInput) {
    debugSeasonEndInput.addEventListener("input", () => {
      if (seasonEndFromServer) {
        return;
      }
      const value = debugSeasonEndInput.value;
      if (!value) {
        seasonEndTime = null;
        lastSeasonMinutes = null;
        seasonEnded = false;
        seasonState.ended = false;
        updateSeasonPanelLabelTexture();
        requestDraw();
        return;
      }
      const parsed = parseDatetimeLocal(value);
      if (!parsed || Number.isNaN(parsed.getTime())) {
        return;
      }
      seasonEndTime = parsed;
      lastSeasonMinutes = null;
      seasonEnded = false;
      seasonState.ended = false;
      updateSeasonPanelLabelTexture();
      requestDraw();
    });
  }
}

function initMockControls() {
  if (mockApplyButton) {
    mockApplyButton.addEventListener("click", async () => {
      if (!runtimeState.useStub) {
        return;
      }
      const ticket = mockTicketInput ? Number(mockTicketInput.value) : null;
      const red = mockRedBagInput ? Number(mockRedBagInput.value) : null;
      const blue = mockBlueBagInput ? Number(mockBlueBagInput.value) : null;
      await setMockEconomy({
        ticket_count: Number.isFinite(ticket) ? ticket : undefined,
        red_bag: Number.isFinite(red) ? red : undefined,
        blue_bag: Number.isFinite(blue) ? blue : undefined,
      });
      syncMockDebugInputs();
    });
  }
  if (mockResetButton) {
    mockResetButton.addEventListener("click", async () => {
      if (!runtimeState.useStub) {
        return;
      }
      await resetMockEconomy();
      syncMockDebugInputs();
    });
  }
}

function saveDebugOptions() {
  // No-op: client persistence disabled.
}

function updateDebugYutButtonsActive() {
  if (!debugYutButtonInputs.length) {
    return;
  }
  for (const button of debugYutButtonInputs) {
    const key = button.dataset.debugYut;
    button.classList.toggle("is-active", key && key === debugOptions.forcedYutKey);
  }
}

function updateDebugYutButtonsPosition(panelRect) {
  if (!debugYutButtons) {
    return;
  }
  const shouldShow =
    debugUiEnabled &&
    debugOptions.fixedYut &&
    titleState.phase === "corner" &&
    yutButtonTexture.texture;
  debugYutButtons.classList.toggle("is-active", shouldShow);
  debugYutButtons.setAttribute("aria-hidden", shouldShow ? "false" : "true");
  if (!shouldShow || !panelRect) {
    return;
  }
  const buttonRect = getYutButtonRect(panelRect);
  const canvasRect = canvas.getBoundingClientRect();
  const scaleX = canvasRect.width / canvas.width;
  const scaleY = canvasRect.height / canvas.height;
  const left = canvasRect.left + buttonRect.left * scaleX;
  const top =
    canvasRect.top +
    (canvas.height - (buttonRect.bottom + buttonRect.height)) * scaleY +
    buttonRect.height * scaleY +
    8;
  debugYutButtons.style.left = `${Math.round(left)}px`;
  debugYutButtons.style.top = `${Math.round(top)}px`;
}

function pickDragPieceAtPoint(point, panelRect) {
  let bestPiece = null;
  let bestDist = Infinity;
  const goalArea = getGoalArea(panelRect);
  for (const piece of rosterState.tokens) {
    if (piece.spawnActive || piece.entryActive || piece.knockActive || piece.finishActive) {
      continue;
    }
    let center = null;
    let hitRect = null;
    if (piece.status === "active" && piece.boxId != null) {
      const box = getBoxById(piece.boxId);
      if (!box) {
        continue;
      }
      hitRect = getBoxRectPixels(box, panelRect);
      center = getBoxCenterPixels(box, panelRect);
    } else if (piece.status === "waiting") {
      const benchCenter = getPieceBenchCenter(piece, panelRect);
      center = benchCenter;
      const size = panelRect.height * 0.08;
      hitRect = {
        left: benchCenter.x - size * 0.5,
        bottom: benchCenter.y - size * 0.5,
        width: size,
        height: size,
      };
    } else if (piece.status === "finished") {
      const goalCenter = getGoalCenterFromOffset(piece, goalArea);
      center = goalCenter;
      const size = panelRect.height * 0.08;
      hitRect = {
        left: goalCenter.x - size * 0.5,
        bottom: goalCenter.y - size * 0.5,
        width: size,
        height: size,
      };
    } else {
      continue;
    }
    if (!hitRect || !center || !pointInRect(point, hitRect)) {
      continue;
    }
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestPiece = piece;
    }
  }
  return bestPiece;
}

function getPieceBenchCenter(piece, panelRect) {
  const bench = getBenchCenters(panelRect);
  const base = getBenchBaseSize(panelRect);
  const teamCenter = piece.team === "player" ? bench.player : bench.monster;
  const offset = piece.offset || { x: 0, y: 0 };
  return {
    x: teamCenter.x + offset.x * base,
    y: teamCenter.y + offset.y * base,
  };
}

function getGoalArea(panelRect) {
  const boardRect = textures.board.texture
    ? computeContainedRect(panelRect, textures.boardSize, 0.9)
    : panelRect;
  const rightEdge = boardRect.left - panelRect.width * 0.02;
  const leftEdge = panelRect.left + panelRect.width * 0.03;
  const width = Math.max(panelRect.width * 0.12, rightEdge - leftEdge);
  const height = panelRect.height * 0.26;
  const bottom = panelRect.bottom + panelRect.height * 0.06;
  return {
    left: leftEdge,
    bottom,
    width,
    height,
  };
}

function getGoalCenterFromOffset(piece, goalArea) {
  const offset = piece.goalOffset || { x: 0.5, y: 0.5 };
  const teamHeight = goalArea.height * 0.5;
  const teamBottom = piece.team === "player" ? goalArea.bottom + teamHeight : goalArea.bottom;
  return {
    x: goalArea.left + goalArea.width * offset.x,
    y: teamBottom + teamHeight * offset.y,
  };
}

function updateSfxControls() {
  if (
    !sfxEnabledToggle &&
    !sfxVolumeInput &&
    !optionsVolumeInput &&
    !sfxUploadButton &&
    !sfxUploadInput &&
    !sfxSelectInputs.length
  ) {
    return;
  }
  const state = getSfxState();
  if (sfxEnabledToggle) {
    sfxEnabledToggle.checked = state.enabled;
  }
  if (sfxVolumeInput) {
    sfxVolumeInput.value = state.volume.toFixed(2);
    sfxVolumeInput.disabled = !state.enabled;
  }
  if (optionsVolumeInput) {
    optionsVolumeInput.value = `${Math.round(state.volume * 100)}`;
    optionsVolumeInput.disabled = !state.enabled;
  }
  if (optionsVolumeValue) {
    optionsVolumeValue.textContent = `${Math.round(state.volume * 100)}`;
  }
  for (const select of sfxSelectInputs) {
    const key = select.dataset.sfxSelect;
    const options = getSfxOptions(key);
    const selection = getSfxSelection(key);
    select.innerHTML = "";
    const defaultGroup = document.createElement("optgroup");
    defaultGroup.label = "DEFAULT";
    const uploadGroup = document.createElement("optgroup");
    uploadGroup.label = "UPLOADS";
    for (const option of options) {
      const el = document.createElement("option");
      el.value = option.id;
      el.textContent = option.name;
      if (option.isDefault) {
        defaultGroup.appendChild(el);
      } else {
        uploadGroup.appendChild(el);
      }
    }
    select.appendChild(defaultGroup);
    if (uploadGroup.children.length) {
      select.appendChild(uploadGroup);
    }
    select.value = selection;
    if (!select.value) {
      const fallback = `default:${key}`;
      select.value = fallback;
      if (!select.value && options.length) {
        select.value = options[0].id;
      }
    }
    select.disabled = !state.enabled;
  }
  if (sfxUploadButton) {
    sfxUploadButton.disabled = !state.enabled;
  }
  if (sfxUploadInput) {
    sfxUploadInput.disabled = !state.enabled;
  }
  for (const button of sfxDeleteButtons) {
    const row = button.closest(".sfx-row");
    const select = row ? row.querySelector("select[data-sfx-select]") : null;
    const selection = select ? select.value : "";
    const isDefault = !selection || selection.startsWith("default:");
    button.disabled = !state.enabled || isDefault;
  }
  updateSfxUploadTargetLabel();
}

function initSfxControls() {
  if (
    !sfxEnabledToggle &&
    !sfxVolumeInput &&
    !optionsVolumeInput &&
    !sfxUploadButton &&
    !sfxUploadInput &&
    !sfxSelectInputs.length
  ) {
    return;
  }
  initSfx();
  if (sfxSelectInputs.length) {
    sfxUploadTargetKey = sfxSelectInputs[0].dataset.sfxSelect || null;
  }
  updateSfxControls();
  restoreSfxSources()
    .then(() => {
      updateSfxControls();
    })
    .catch(() => {});
  if (sfxEnabledToggle) {
    sfxEnabledToggle.addEventListener("change", () => {
      setSfxEnabled(sfxEnabledToggle.checked);
      updateSfxControls();
    });
  }
  if (sfxVolumeInput) {
    const onVolume = () => {
      ensureSfxUnlocked();
      setSfxVolume(Number(sfxVolumeInput.value));
      updateSfxControls();
    };
    sfxVolumeInput.addEventListener("input", onVolume);
    sfxVolumeInput.addEventListener("change", onVolume);
  }
  if (optionsVolumeInput) {
    const onVolume = () => {
      ensureSfxUnlocked();
      setSfxVolume(Number(optionsVolumeInput.value) / 100);
      updateSfxControls();
    };
    optionsVolumeInput.addEventListener("input", onVolume);
    optionsVolumeInput.addEventListener("change", onVolume);
  }
  if (optionsVolumeDown) {
    optionsVolumeDown.addEventListener("click", () => {
      ensureSfxUnlocked();
      const current = Number(optionsVolumeInput?.value) || 0;
      const next = Math.max(0, current - 5);
      setSfxVolume(next / 100);
      updateSfxControls();
    });
  }
  if (optionsVolumeUp) {
    optionsVolumeUp.addEventListener("click", () => {
      ensureSfxUnlocked();
      const current = Number(optionsVolumeInput?.value) || 0;
      const next = Math.min(100, current + 5);
      setSfxVolume(next / 100);
      updateSfxControls();
    });
  }
  if (sfxSelectInputs.length) {
    for (const select of sfxSelectInputs) {
      select.addEventListener("focus", () => {
        sfxUploadTargetKey = select.dataset.sfxSelect || sfxUploadTargetKey;
        updateSfxUploadTargetLabel();
      });
      select.addEventListener("change", () => {
        const key = select.dataset.sfxSelect;
        if (key) {
          setSfxSelection(key, select.value);
          sfxUploadTargetKey = key;
          updateSfxControls();
        }
      });
    }
  }
  if (sfxDeleteButtons.length) {
    for (const button of sfxDeleteButtons) {
      button.addEventListener("click", async () => {
        const row = button.closest(".sfx-row");
        const select = row ? row.querySelector("select[data-sfx-select]") : null;
        if (!select) {
          return;
        }
        const selectionId = select.value;
        if (!selectionId || selectionId.startsWith("default:")) {
          return;
        }
        const entry = getSfxEntryInfo(selectionId);
        if (!entry) {
          return;
        }
        const state = getSfxState();
        const usedBy = state.keys.filter((key) => getSfxSelection(key) === selectionId);
        if (usedBy.length) {
          const confirmText = `Delete "${entry.name}"?\nUsed by: ${usedBy.join(", ")}`;
          if (!window.confirm(confirmText)) {
            return;
          }
        }
        button.disabled = true;
        await deleteSfxEntry(selectionId);
        for (const key of usedBy) {
          setSfxSelection(key, `default:${key}`);
        }
        updateSfxControls();
      });
    }
  }
  if (sfxUploadButton && sfxUploadInput) {
    sfxUploadButton.addEventListener("click", () => {
      if (!sfxUploadTargetKey || sfxUploadInput.disabled) {
        return;
      }
      sfxUploadInput.click();
    });
    sfxUploadInput.addEventListener("change", async () => {
      const file = sfxUploadInput.files && sfxUploadInput.files[0];
      if (file && sfxUploadTargetKey) {
        sfxUploadButton.disabled = true;
        sfxUploadInput.disabled = true;
        await uploadSfxFile(sfxUploadTargetKey, file);
        updateSfxControls();
      }
      sfxUploadInput.value = "";
    });
  }
}

function updateSfxUploadTargetLabel() {
  if (!sfxUploadTarget) {
    return;
  }
  if (!sfxUploadTargetKey && sfxSelectInputs.length) {
    sfxUploadTargetKey = sfxSelectInputs[0].dataset.sfxSelect || null;
  }
  const targetKey = sfxUploadTargetKey;
  if (!targetKey) {
    sfxUploadTarget.textContent = "";
    return;
  }
  const select = sfxSelectInputs.find((entry) => entry.dataset.sfxSelect === targetKey);
  const label = select ? select.closest(".sfx-row")?.querySelector("label") : null;
  const name = label ? label.textContent.trim() : targetKey;
  sfxUploadTarget.textContent = `Target: ${name}`;
}

canvas.addEventListener("contextmenu", (event) => {
  event.preventDefault();
});
canvas.addEventListener("pointerdown", onPointerDown);
canvas.addEventListener("pointermove", onPointerMove);
canvas.addEventListener("pointerup", onPointerUp);
canvas.addEventListener("pointercancel", onPointerUp);

window.addEventListener("keydown", (event) => {
  if (isTypingInInput()) {
    return;
  }
  if (event.key === "Escape") {
    if (helpPopupActive) {
      setHelpPopupActive(false);
      event.preventDefault();
    }
    return;
  }
  ensureSfxUnlocked();
  if (titleState.phase !== "corner") {
    return;
  }
  if (event.code === "Space") {
    if (isPlayerThrowReady()) {
      requestServerAction({ type: "throw" });
    }
    event.preventDefault();
    return;
  }
});

window.addEventListener("resize", () => {
  editor.lastClickTime = 0;
  editor.lastClickBoxId = null;
  requestWinRestartLayout();
  requestDraw();
});

initRuntimeFromUrl();
initRosterTokens();
initYutButtonLabel();
initAutoToggleLabel();
updateYutThrowUI();
loadLoadingSpinner();
loadAtlas();
initSfxControls();
applySfxVolumeFromUrl();
initLanguageOptions();
initSelectionPromptLabel();
initSidePanelLabels();
loadHelpMarkdown();
loadEditorDataFromFile().then(() => {
  setStartBoxId(editor.startBoxId);
  requestDraw();
  bootstrapRuntime();
});
